import { useState, useEffect, useMemo } from 'react';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  Book, 
  Users, 
  Calendar, 
  Bell, 
  TrendingUp, 
  Settings, 
  HelpCircle,
  ChevronRight,
  ArrowUp,
  Search,
  Clock,
  Car,
  CheckCircle2,
  AlertTriangle,
  MapPin,
  Wrench,
  Shield,
  Mail,
  Phone,
  Zap,
  BarChart3,
  FileText,
  Timer,
  UserCog,
  LayoutDashboard,
  Columns3,
  Lock,
  Unlock,
  RotateCcw,
  Brain,
  Eye,
  Maximize2,
  ParkingCircle,
  Gauge,
  Upload,
  CreditCard,
  Info,
  AlertCircle,
  Bug,
  Edit3,
  GraduationCap,
  Activity,
  Archive,
  XCircle,
  LineChart,
  CloudRain,
  Award,
  Target,
  BookOpen,
  Lightbulb,
  Sparkles,
  Library,
  TrendingDown,
  Stethoscope,
  DollarSign,
  PauseCircle,
  Printer,
  ClipboardList,
  PlayCircle,
  Globe,
  Split,
  Thermometer,
  ExternalLink,
  Video,
  BookMarked,
  UserCheck,
  Layers,
  CheckSquare,
  Trash2,
  CreditCard as CreditCardIcon,
  Receipt,
  UserCircle,
  Database,
  Inbox,
  Smartphone,
  ClipboardCheck,
  ScanLine,
  Compass
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useFeatureStore } from '@/stores/featureStore';
import { useAuth } from '@/contexts/AuthContext';
import { canViewGuideItem, type GuideRole } from '@/lib/userGuideVisibility';
import type { FeatureKey } from '@/lib/features/featureFlags';

/** Roles relevant to User Guide visibility (subset of app roles). */
interface GuideSubsection {
  id: string;
  title: string;
  content: React.ReactNode;
  /** Feature key(s) required to view this section. If array, ANY match grants access. */
  featureGate?: FeatureKey | FeatureKey[];
  /**
   * Manual Batch 3 — optional role gating. Omitted ⇒ visible to every
   * route-allowed role (preserves prior behavior). Present ⇒ the viewer's
   * role must be in the list. `featureGate` and `requiredRoles` are ANDed.
   */
  requiredRoles?: GuideRole[];
  /** Optional product-area tag (informational; e.g. "Billing", "Analytics"). */
  productArea?: string;
  /** Optional lifecycle status. Used for labels only — never hides in this batch. */
  status?: 'live' | 'coming_soon' | 'deferred';
  /** Optional tier / availability label, rendered as a small badge. */
  tierLabel?: 'CORE' | 'CATALYST' | 'ENTERPRISE' | 'ADD_ON' | 'FUTURE';
}

interface GuideSection {
  id: string;
  title: string;
  icon: any;
  subsections: GuideSubsection[];
}

// TDO-1 (K70): `canViewGuideItem` + `GuideRole` moved to
// `@/lib/userGuideVisibility` so the predicate is unit-testable without
// importing this page. Behavior unchanged.

const UserGuide = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { featureFlags } = useFeatureStore();
  // Manual Batch 3 — current viewer role drives role-aware subsection gating.
  const { membership } = useAuth();
  const role = membership?.role as GuideRole | undefined;

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // TDO-1 (K70): per-subsection role gating is implemented — `canViewGuideItem`
  // ANDs `featureGate` + `requiredRoles` (conservative: hides when the viewer's
  // role is unknown) and filters subsections at render. Admin/billing/
  // permissions subsections (roles-permissions, billing-subscription,
  // team-management, training-catalog-admin, invoice-upload-csv, custody, …)
  // carry `requiredRoles` so they are no longer shown to every route-allowed
  // role. General user help intentionally stays ungated/visible.
  const sections: GuideSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Book,
      subsections: [
        {
          id: 'account-setup',
          title: 'Account Setup',
          content: (
            <div className="space-y-4">
              <p>Welcome to OptiBay Core! Here's how to get your account configured:</p>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>Sign up with your email address</li>
                <li>Complete the shop onboarding wizard</li>
                <li>Configure your shop hours and holidays</li>
                <li>Set up your team members and their roles</li>
              </ol>
              <div className="bg-muted p-4 rounded-lg mt-4">
                <p className="text-sm"><strong>Tip:</strong> Make sure to complete all onboarding steps for full system functionality.</p>
              </div>
            </div>
          )
        },
        {
          id: 'roles-permissions',
          title: 'Roles & Permissions',
          // TDO-1: permissions/admin help — gate to admin roles (was shown to every role).
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN'],
          content: (
            <div className="space-y-4">
              <p>The system uses Role-Based Access Control (RBAC) with three main roles:</p>
              <div className="space-y-3 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Manager
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    Full system access including team management, billing, analytics, and all configurations.
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCog className="h-4 w-4" />
                      Service Writer
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    Create and manage tickets, view analytics, manage technicians, but cannot access billing or team settings.
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Technician
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    View assigned tickets, manage job timers, update their own schedule and availability.
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'shop-profile',
          title: 'Shop Profile Setup',
          content: (
            <div className="space-y-4">
              <p>Configure your shop's core settings in the Shop Profile page:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Business Hours:</strong> Set opening and closing times for each day</li>
                <li><strong>Holidays:</strong> Add shop closure dates to prevent scheduling</li>
                <li><strong>Vehicle Types:</strong> Select which vehicle types your shop services (Asian, Domestic, Diesel, Euro, EV, Exotic)</li>
                <li><strong>Timezone:</strong> Set your shop's timezone for accurate scheduling</li>
                <li><strong>Break Times:</strong> Configure standard break schedules for your team</li>
              </ul>
            </div>
          )
        },
        {
          id: 'interactive-tutorial',
          title: 'Interactive Tutorial',
          content: (
            <div className="space-y-4">
              <p>OptiBay includes an interactive onboarding walkthrough. Which one you get depends on your role, and the walkthrough leaves out anything your role cannot do — so you are not shown controls that are not there.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      Walkthroughs by role
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold">Manager and owner:</p>
                      <p className="text-muted-foreground">The full setup walkthrough — shop profile, hours, floor layout, team invitations, technician profiles and analytics. It is long because setting a shop up is long.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Service advisor and front of house:</p>
                      <p className="text-muted-foreground">The board, creating a ticket, assigning it, advisor custody, finding a ticket again, and customer records. Steps covering something you do not have permission for are left out rather than shown and disabled.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Technician:</p>
                      <p className="text-muted-foreground">Your work list, opening a ticket, starting the timer, pausing when you step away, and stopping only when the work is genuinely done — because stopping is what completes the ticket.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Organisation-level roles:</p>
                      <p className="text-muted-foreground">A short orientation rather than shop instruction, because day-to-day Core actions belong to the shop roles.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Roles without a walkthrough yet:</p>
                      <p className="text-muted-foreground">Dispatcher, parts and auditor accounts do not currently receive one. The lessons exist, but these roles are not yet granted access to the pages those lessons take place on, and we would rather say so than send you to a page that refuses you. This page remains available to roles that can open it.</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Tutorial Features</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Progress bar:</strong> a thin bar under the top navigation. It counts against <em>your</em> walkthrough, so if steps were left out because they do not apply to you, the total reflects that rather than a length you could never reach.</li>
                      <li><strong>Coachmarks:</strong> Highlighted UI elements with spotlight effect and step-by-step instructions</li>
                      <li><strong>Step Bubbles:</strong> Compact tooltips near highlighted elements with guidance and action buttons</li>
                      <li><strong>Checklist Dialog:</strong> View all tutorial steps and track progress</li>
                      <li><strong>Minimizable:</strong> Click minimize to complete steps without blocking your view</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing the Tutorial</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>First visit:</strong> the walkthrough starts by itself, once, for roles that have one. If your role has none, nothing opens and you are told why rather than shown an empty tour.</li>
                      <li><strong>Replay Tutorial:</strong> Click the Help Menu button (appears after tutorial completion)</li>
                      <li><strong>View Progress:</strong> Click "Checklist" in the tutorial HUD to see all steps</li>
                      <li><strong>Skip Steps:</strong> Click "Skip" on any step (step marked as skipped, not completed)</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'core-features',
      title: 'Core Features',
      icon: Zap,
      subsections: [
        {
          id: 'shop-overview',
          title: 'Shop Overview (Dashboard)',
          content: (
            <div className="space-y-4">
              <p>The Dashboard is your central hub for viewing all tickets, technicians, and operations:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      Main Tabs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold">Overview Tab:</p>
                      <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                        <li>Statistics cards showing active tickets, pending work, completed today</li>
                        <li>Technician presence indicators</li>
                        <li>Quick links to completed tickets</li>
                        <li>Real-time workload calculations</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Dayboard Tab:</p>
                      <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                        <li>Primary work management view with technician columns</li>
                        <li>Parking Lot for unassigned tickets</li>
                        <li>Drag-and-drop ticket assignment</li>
                        <li>Real-time timer tracking</li>
                        <li>Compact vs. expanded view toggle</li>
                        <li>Fullscreen mode option</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Specialty Bays Tab:</p>
                      <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                        <li>Dedicated view for alignment, ADAS, wash bays</li>
                        <li>Shows only tickets requiring specialty equipment</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Parking Lot Tab:</p>
                      <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                        <li>Archive of completed and multi-day tickets</li>
                        <li>Rollover management</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Create new tickets</li>
                      <li>Add jobs to existing tickets</li>
                      <li>Manage technicians and schedules</li>
                      <li>Access calendar to jump to different dates</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'dayboard-navigation',
          title: 'Dayboard Navigation & Interaction',
          content: (
            <div className="space-y-4">
              <p>The Dayboard is your primary tool for managing daily work:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Columns3 className="h-4 w-4" />
                      Column Layout
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Each technician has their own column showing assigned tickets</li>
                      <li>Columns display workload percentage and capacity</li>
                      <li>Staff presence indicators (green dot = present)</li>
                      <li>Columns can be reordered by dragging technician names</li>
                      <li>View technician efficiency and scheduled breaks</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ticket Card Details</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Color-Coded Status:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Active (blue): Timer running</li>
                      <li>Paused (yellow): Work stopped, time frozen</li>
                      <li>Completed (green): All jobs finished</li>
                    </ul>
                    <p className="mt-3"><strong>Card Information:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Vehicle information and customer name</li>
                      <li>Job list with individual timers</li>
                      <li>Timer controls (Play/Pause/Stop)</li>
                      <li>OptiFinish time (predicted completion)</li>
                      <li>Break indicators when work spans lunch/break time</li>
                    </ul>
                    <p className="mt-3"><strong>Special Badges:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>🔄 Rollover: Ticket continued from previous day</li>
                      <li>🚨 Comeback: Warranty/redo work</li>
                      <li>✂️ Split: Ticket split across multiple days/techs</li>
                      <li>🔧 Alignment: Requires alignment bay</li>
                      <li>📡 ADAS: Requires ADAS calibration bay</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Drag & Drop Functionality</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Drag tickets between technicians to reassign</li>
                      <li>Drag to Parking Lot to unassign</li>
                      <li>Preview new start times during drag</li>
                      <li>Automatic schedule recalculation on drop</li>
                      <li>Cascading time updates for all following tickets</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ParkingCircle className="h-4 w-4" />
                      Parking Lot
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Holds unassigned tickets</li>
                      <li>Auto-collapse for cleaner view</li>
                      <li>Quick assign via drag-and-drop or OptiPlace button</li>
                      <li>Shows all Drop Off tickets awaiting assignment</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'birdseye-view',
          title: 'Birdseye View',
          content: (
            <div className="space-y-4">
              <p>Visual shop floor layout with live bay status and ticket information:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Purpose & Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Visual Bay Layout:</strong> See physical shop arrangement</li>
                      <li><strong>Bay Status Indicators:</strong> Color-coded by bay type</li>
                      <li><strong>Live Ticket Display:</strong> Shows active ticket in each bay</li>
                      <li><strong>Technician Assignment:</strong> See which tech is in each bay</li>
                      <li><strong>Timer Integration:</strong> Real-time countdown on bay cards</li>
                      <li><strong>Pan & Zoom:</strong> Drag to navigate large shop layouts</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Set up your shop floor layout in the <strong>Shop Floor Editor</strong>. Configure bay positions, dimensions, and types to match your physical shop.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Use Cases</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Quick visual status check of entire shop</li>
                      <li>Customer updates on vehicle location</li>
                      <li>Capacity planning and resource allocation</li>
                      <li>Identifying bottlenecks in workflow</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'creating-tickets',
          title: 'Creating & Managing Tickets',
          content: (
            <div className="space-y-4">
              <p>Tickets are the foundation of the scheduling system. Here's how to work with them:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Creating a Ticket</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      <li>Click "Create Ticket" in the top navigation</li>
                      <li>Enter customer information (name, phone, email)</li>
                      <li>Add vehicle details using VIN decoder or manual entry</li>
                      <li>Select jobs from the catalog or create custom jobs</li>
                      <li>Set ticket type (Waiter or Drop Off)</li>
                      <li>Review and submit</li>
                    </ol>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">VIN Decoder</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Enter a 17-character VIN to automatically populate vehicle year, make, model, and engine information from our comprehensive vehicle catalog.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ticket Types</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Waiter:</strong> Customer is waiting, scheduled for same-day completion</p>
                    <p><strong>Drop Off:</strong> Vehicle left at shop, flexible scheduling</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Edit3 className="h-4 w-4" />
                      Edit Ticket Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>After creating a ticket, you can modify key details using the ticket kebab menu (⋮):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Ticket Type:</strong> Switch between Waiter and Drop-off (automatically clears scheduled start time when switching to Drop-off)</li>
                      <li><strong>Scheduled Start Time:</strong> Adjust when work should begin (disabled for Drop-off tickets)</li>
                      <li><strong>Due Out Time:</strong> Update the promised completion time</li>
                      <li><strong>Comeback Status:</strong> Mark ticket as comeback/warranty work</li>
                    </ul>
                    <p className="mt-2">Click the <strong>Edit Details</strong> option (Clock icon) in any ticket's kebab menu to open the editor.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'inline-job-editing',
          title: 'Inline Job Editing',
          content: (
            <div className="space-y-4">
              <p>Quickly edit job details directly from the ticket card without opening the full editor:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Edit3 className="h-4 w-4" />
                      How to Access
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Click any job line on a ticket card in the Dayboard to open the inline editor. Job lines show a subtle underline and color change on hover to indicate they're clickable.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Editable Fields</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Estimated Hours:</strong> Override the default job time estimate (displayed in hours, stored as minutes)</li>
                      <li><strong>Notes:</strong> Add technician notes or special instructions for the job</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Changes are saved immediately and update OptiFinish calculations in real-time.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Visual Indicators</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Jobs with overridden times display differently to show custom estimates. The inline editor shows the original catalog time for reference.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Permissions</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Managers and Service Writers can edit job details. Technicians can view workflow notes but cannot modify times.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'comeback-tickets',
          title: 'Comeback Functionality',
          content: (
            <div className="space-y-4">
              <p>Handle warranty and redo work with special priority:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <RotateCcw className="h-4 w-4" />
                      What is a Comeback Ticket?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Warranty or redo work on a vehicle that was previously serviced. These tickets ensure continuity and accountability.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Marking as Comeback</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>When creating a ticket, check the "Comeback" box. The ticket will display a 🚨 badge on the dayboard.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Special Handling</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>System prioritizes assigning to the original technician who worked on the vehicle</li>
                      <li>"Comeback Nudge" setting in Shop Culture controls strength of this preference</li>
                      <li>Helps maintain accountability and customer satisfaction</li>
                      <li>Historical tracking remembers previous technician assignments</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'comeback-resolution-modal',
          title: 'Comeback Resolution Modal',
          content: (
            <div className="space-y-4">
              <p>When completing a ticket marked as a comeback, the system requires you to identify the root cause before finalizing:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      When It Appears
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Comeback Resolution Modal automatically opens when you stop the timer on any ticket that has the "Comeback" flag enabled. You must select a resolution before the ticket can be marked complete.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Resolution Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-2">
                      <li><strong>Parts Failure:</strong> The issue was caused by a defective or failed part. No technician penalty applied.</li>
                      <li><strong>Technician Fault:</strong> The original work was not performed correctly. <span className="text-destructive">This automatically decreases the original technician's proficiency scores.</span></li>
                      <li><strong>Unrelated to Prior Repair:</strong> The new issue is coincidental and unrelated to previous work. No penalty applied.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Impact on Proficiency</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When "Technician Fault" is selected:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>The original technician's proficiency scores are reduced</li>
                      <li>Affected categories are based on the job types in the original ticket</li>
                      <li>Changes sync to the technician's global profile</li>
                      <li>Historical record is maintained in their Passport</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Visual Indicators</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>After resolution, completed tickets display a badge showing the resolution type (Parts, Tech Fault, or Unrelated) for future reference.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'vehicle-present',
          title: 'Vehicle Presence (Derived)',
          content: (
            <div className="space-y-4">
              <p>Vehicle presence is now <strong>automatically derived</strong> — no manual toggle required.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How It Works</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>A vehicle is considered present when any of these conditions are true:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>The ticket has a <strong>parking spot</strong> assigned</li>
                      <li>The ticket has a <strong>bay</strong> assigned</li>
                      <li>The ticket has an <strong>active timer</strong> (running or paused)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Impact on Scheduling</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Present vehicles get priority boost (configurable in Shop Culture)</li>
                      <li>Assigning a parking spot or bay automatically marks the vehicle as present</li>
                      <li>Removing all location assignments marks the vehicle as not present</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'diagnostic-outcome-modal',
          title: 'Diagnostic Outcome Modal',
          content: (
            <div className="space-y-4">
              <p>When completing tickets containing diagnostic or inspection jobs, the system tracks accuracy to improve technician assignments:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      When It Appears
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Diagnostic Outcome Modal automatically opens when stopping the timer on any ticket that contains jobs tagged as "diagnostic" or "inspection" in the job catalog. Both types trigger this modal since inspections are essentially low-level diagnostics.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Outcome Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>For each diagnostic/inspection job on the ticket, select one outcome:</p>
                    <ul className="list-disc list-inside space-y-2">
                      <li><strong>Confirmed (100%):</strong> The diagnosis was accurate and the issue was verified</li>
                      <li><strong>Partial (50%):</strong> The diagnosis was partially correct or additional issues were found</li>
                      <li><strong>Misdiagnosis (0%):</strong> The diagnosis was incorrect and the issue was something else</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Impact on Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Outcomes directly affect the technician's <strong>Diagnostic Accuracy</strong> metric:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Confirmed outcomes add 100% to accuracy calculations</li>
                      <li>Partial outcomes add 50% to accuracy calculations</li>
                      <li>Misdiagnosis outcomes add 0% to accuracy calculations</li>
                      <li>Running average displayed in Technician Passport under Performance DNA</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Mandatory Completion</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The modal is mandatory before ticket completion proceeds. All diagnostic/inspection jobs must have an outcome selected. This ensures accurate data collection for the learning system.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'manual-ticket-split',
          title: 'Manual Ticket Split',
          content: (
            <div className="space-y-4">
              <p>Split multi-job tickets across different technicians or days for optimal workload distribution:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Split className="h-4 w-4" />
                      When to Use Split
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Large tickets that won't complete in one day</li>
                      <li>Jobs requiring different skill specializations</li>
                      <li>Balancing workload when one technician is overloaded</li>
                      <li>Separating diagnostic work from repair work</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How to Split a Ticket</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ol className="list-decimal list-inside ml-2 space-y-1">
                      <li>Click the kebab menu (⋮) on any ticket card with 2+ jobs</li>
                      <li>Select <strong>"Split Ticket"</strong></li>
                      <li>Choose which jobs go to each part (Part A stays, Part B moves)</li>
                      <li>Select technician and time slot for Part B</li>
                      <li>Confirm the split</li>
                    </ol>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Split Requirements & Constraints</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Timer Guard:</strong> Cannot split while ticket timer is actively running</li>
                      <li><strong>Minimum Jobs:</strong> Ticket must have at least 2 jobs to split</li>
                      <li><strong>Bay Compatibility:</strong> Each part must have valid bay assignments</li>
                      <li><strong>Due Out Respect:</strong> Both parts must finish before original due_out time</li>
                    </ul>
                    <Alert className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle className="text-xs">Pause First</AlertTitle>
                      <AlertDescription className="text-xs">
                        If the timer is running, you'll see "Please pause or stop this ticket's timer before splitting it."
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Field Inheritance</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Part B inherits all parent ticket data:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1 text-xs">
                      <li>Vehicle and customer information</li>
                      <li>Flags: is_waiter, is_comeback</li>
                      <li>Priority/severity settings</li>
                      <li>Parts delay status and blocked_until</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Split Badge & Tracking</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Split tickets display a ✂️ badge and maintain linkage for audit trails. Both parts show connection to their counterpart for complete visibility.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'specialty-bay-routing',
          title: 'Specialty Bay Routing',
          content: (
            <div className="space-y-4">
              <p>Intelligent routing of tickets to specialized equipment bays based on job requirements:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Bay Types
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>General:</strong> Standard repair bays for most work</li>
                      <li><strong>Alignment:</strong> Equipped with alignment racks</li>
                      <li><strong>ADAS:</strong> ADAS calibration equipment and targets</li>
                      <li><strong>Wash:</strong> Vehicle washing/detailing bays</li>
                      <li><strong>Outdoor:</strong> Open-air bays (weather-dependent)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Automatic Bay Assignment</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When a ticket contains specialty jobs, the system:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Identifies required bay type from job catalog</li>
                      <li>Filters available technicians to those assigned specialty bays</li>
                      <li>Schedules specialty phases during bay availability windows</li>
                      <li>Prevents double-booking of specialty equipment</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Multi-Phase Tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Tickets with both general and specialty work are phased:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Phase 1:</strong> General repair work in standard bay</li>
                      <li><strong>Phase 2:</strong> Alignment in alignment bay (if needed)</li>
                      <li><strong>Phase 3:</strong> ADAS calibration in ADAS bay (if needed)</li>
                    </ul>
                    <p className="text-muted-foreground text-xs mt-2">Phases may be reordered based on bay availability and optimal workflow.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Visual Indicators</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Tickets requiring specialty bays display badges on the Dayboard:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                      <li>🔧 <strong>Alignment</strong>: Requires alignment rack</li>
                      <li>📡 <strong>ADAS</strong>: Requires ADAS calibration bay</li>
                      <li>🚿 <strong>Wash</strong>: Requires wash bay</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Specialty Bays Tab</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Access the <strong>Specialty Bays</strong> tab from the Dashboard to see a dedicated view of alignment, ADAS, and wash bay schedules with only tickets requiring that equipment.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'ticket-locking',
          title: 'Ticket Locking & Timer Controls',
          content: (
            <div className="space-y-4">
              <p>Understanding when timers lock and how to unlock them:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      When Timers Lock
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Vehicle Not Present:</strong></p>
                    <p className="ml-4">Must mark vehicle as present before starting timers. Shows 🔒 lock icon.</p>
                    <p className="mt-2"><strong>Rollover Predecessor Incomplete:</strong></p>
                    <p className="ml-4">For split tickets, previous day's work must complete first. Prevents timing errors.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Unlock className="h-4 w-4" />
                      Unlocking Process
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Vehicle Present Lock:</strong> Assign a parking spot or bay to the ticket</li>
                      <li><strong>Rollover Lock:</strong> Complete or pause predecessor ticket</li>
                      <li>Lock status updates in real-time across all views</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Timer States</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Not Started (gray):</strong> Timer hasn't begun</li>
                      <li><strong>Active (blue, pulsing):</strong> Timer running, work in progress</li>
                      <li><strong>Paused (yellow):</strong> Work stopped, time frozen</li>
                      <li><strong>Stopped (red):</strong> Work complete</li>
                    </ul>
                    <p className="mt-2"><strong>Pause Reasons:</strong> Waiting for parts, customer approval, tech on break, vehicle testing, etc.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'technician-management',
          title: 'Technician Management',
          content: (
            <div className="space-y-4">
              <p>Manage your team's skills, availability, and assignments:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Skill Tiers:</strong> Rate technicians from 1-10 across different specialties (General, Diagnostics, Electrical, etc.)</li>
                <li><strong>Availability:</strong> Set daily schedules and recurring break times</li>
                <li><strong>Bay Assignments:</strong> Assign technicians to specific bays for optimal workflow</li>
                <li><strong>Multi-Bay Support:</strong> Configure which technicians can work across multiple bays</li>
              </ul>
              <div className="bg-muted p-4 rounded-lg mt-4">
                <p className="text-sm"><strong>Pro Tip:</strong> Higher skill tier technicians are prioritized for complex jobs by the auto-assignment system.</p>
              </div>
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Archive className="h-4 w-4" />
                    Archiving Technicians
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <p>When removing a technician from your team, the system uses a soft-delete/archive pattern to preserve historical data:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li><strong>Status Change:</strong> Technician's employment_status is updated to "Inactive"</li>
                    <li><strong>Historical Records:</strong> All past ticket assignments, completions, and analytics remain intact</li>
                    <li><strong>Hidden from Scheduling:</strong> Archived technicians don't appear in assignment flows or dayboard</li>
                    <li><strong>Audit Trail:</strong> Maintains data integrity for reporting and accountability</li>
                  </ul>
                  <p className="mt-2">To archive a technician, navigate to Team Management and remove them from the team members list.</p>
                </CardContent>
              </Card>
            </div>
          )
        },
        {
          id: 'technician-passport',
          title: 'Technician Passport (Auror Codex)',
          featureGate: 'technicianPassports',
          content: (
            <div className="space-y-4">
              <p>The <strong>Auror Codex</strong> is a comprehensive performance passport for each technician, providing deep insights into skills, performance history, and coaching opportunities.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Award className="h-4 w-4" />
                      Accessing the Passport
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Navigate to the Technician Passport via:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1">
                      <li>Go to <strong>Shop Profile → Technicians tab</strong></li>
                      <li>Click <strong>Edit</strong> on any technician</li>
                      <li>Click the <strong>"View Tech Passport"</strong> button</li>
                    </ol>
                    <p className="mt-2 text-muted-foreground">Or click on a technician's name in the Analytics → Technicians tab.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Core Passport Sections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold">Snapshot Card</p>
                      <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                        <li>Auto-generated headline based on top skills</li>
                        <li>Overall letter grade (A+ to C) based on performance</li>
                        <li>Strength and stretch area tags</li>
                        <li>AI-written performance summary</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Category Proficiencies (Radar Chart)</p>
                      <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                        <li>Visual radar chart showing skill levels across 18 categories</li>
                        <li>Collapsible groups: Mechanical Systems, Electrical & Diagnostics, Chassis Systems, Maintenance & Inspection</li>
                        <li>Individual skill bars with score, confidence, and sample size</li>
                        <li>Verdict badges: Strong Fit, Capable, Developing, Avoid</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Performance DNA</p>
                      <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                        <li>Efficiency rating: Percentage of jobs completed on or under estimated time</li>
                        <li>Comeback rate: Percentage of warranty/redo work</li>
                        <li>Diagnostic accuracy: Success rate on diagnostic jobs</li>
                        <li>30/90-day trend charts showing performance over time</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Employment History</p>
                      <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                        <li>Complete work history across shops</li>
                        <li>Tenure and employment status tracking</li>
                        <li>Portable across OptiBay shops</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Skill Heatmap</p>
                      <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                        <li>Visual grid showing proficiency across all 18 job categories</li>
                        <li>Color-coded intensity based on skill level</li>
                        <li>Quick identification of strengths and gaps</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold">Time Signature</p>
                      <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                        <li>Breakdown of time allocation across job types</li>
                        <li>Historical time patterns and trends</li>
                        <li>Average completion times by category</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      OptiTime Insights <Badge variant="outline" className="ml-2 text-xs">CATALYST</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>AI-powered learning insights from job history:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Learned Combos:</strong> Vehicle + job combinations the system has learned</li>
                      <li><strong>Strength Patterns:</strong> Jobs where technician consistently beats estimates</li>
                      <li><strong>Growth Edges:</strong> Areas where performance could improve</li>
                      <li><strong>Time Delta:</strong> Average difference from estimated vs actual times</li>
                    </ul>
                    <p className="text-muted-foreground text-xs mt-2">Insights emerge as more repeat jobs are completed on the same vehicle types.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Lightbulb className="h-4 w-4" />
                      OptiTime Coaching <Badge variant="outline" className="ml-2 text-xs">CATALYST</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Actionable coaching recommendations based on performance patterns:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Strength Highlights:</strong> Areas where technician excels</li>
                      <li><strong>Growth Opportunities:</strong> Specific skills to develop</li>
                      <li><strong>Trend Analysis:</strong> Overall performance trajectory</li>
                    </ul>
                    <p className="text-muted-foreground text-xs mt-2">Coaching insights update automatically as new data is collected.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Diagnostic Competency <Badge variant="outline" className="ml-2 text-xs">CATALYST · Manager Only</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Specialized diagnostic performance tracking (visible to managers only):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>DCS (Diagnostic Competency Score):</strong> 0-100 composite score</li>
                      <li><strong>Calibration Score:</strong> Confidence vs outcome alignment</li>
                      <li><strong>Outcome Success Rate:</strong> Percentage of confirmed diagnoses</li>
                      <li><strong>Complaint Validation:</strong> Customer complaint confirmation rate</li>
                      <li><strong>Avg Diagnostic Time:</strong> Efficiency on diagnostic jobs</li>
                    </ul>
                    <div className="mt-2 space-y-1">
                      <p className="font-semibold text-xs">Quality Tiers:</p>
                      <ul className="list-disc list-inside ml-2 text-xs text-muted-foreground">
                        <li>Exceptional (85+) | Strong (70-84) | Developing (50-69) | Needs Support (&lt;50)</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ticket Ledger</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                      <li>Complete job history with filtering options</li>
                      <li>Timeline and table view options</li>
                      <li>Job mix donut chart by category</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Future Insights</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Predictive performance forecasting:</p>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                      <li>Projected efficiency trends</li>
                      <li>Skill development trajectory</li>
                      <li>Potential coaching focus areas</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Learning Center</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Integrated training and development resources:</p>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                      <li>Recommended training based on performance gaps</li>
                      <li>Learning history tracking</li>
                      <li>Training artifact library access</li>
                    </ul>
                    <p className="text-muted-foreground text-xs mt-2">See "Learning Center" section under Advanced Features for full details.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Coaching Notes (Manager Only)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 text-muted-foreground text-xs">
                      <li>Add timestamped coaching feedback</li>
                      <li>Notes are permanently saved and immutable</li>
                      <li>Maintains audit trail for performance reviews</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Proficiency Learning
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The system automatically learns and updates technician proficiencies based on:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Job Completions:</strong> Efficiency on completed jobs updates category scores</li>
                      <li><strong>Comeback Resolutions:</strong> Technician fault comebacks decrease proficiency</li>
                      <li><strong>Diagnostic Outcomes:</strong> Confirmed/Partial/Misdiagnosis results tracked</li>
                      <li><strong>Adaptive Learning:</strong> New techs learn faster, experienced techs stabilize</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'job-timers',
          title: 'Job Timers & Tracking',
          content: (
            <div className="space-y-4">
              <p>Track actual time spent on jobs for accurate scheduling and performance metrics:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Timer Controls</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Start:</strong> Begin tracking time on a job</p>
                    <p><strong>Pause:</strong> Temporarily stop the timer. You pick a pause reason (e.g. waiting on parts, customer approval, break, vehicle testing). Accumulated time stays with the job; use <strong>Resume</strong> to continue.</p>
                    <p><strong>Resume:</strong> Continue timing from where you paused</p>
                    <p><strong>Stop &amp; Complete:</strong> Stop the timer, mark the ticket complete, and move it to the Parking Lot. Completion and parking behavior are unchanged.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Timer States</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Timers persist across jobs on the same ticket. When you pause a job, the accumulated time stays with that specific job.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Optional: Also kick to FOH</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Both the Pause and the Stop dialogs include an optional <strong>"Also kick to FOH"</strong> checkbox. The timer action is always primary; the FOH handoff is secondary and only happens after the timer action succeeds.</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Pause + Kick to FOH</strong> — the tech is blocked: the front office must act before work can continue (e.g. needs estimate, customer approval, parts/pricing).</li>
                      <li><strong>Stop + Kick to FOH</strong> — the currently authorized/scheduled tech work is complete and FOH owns the next step (e.g. ready for invoice, ready for pickup, sell/schedule later).</li>
                    </ul>
                    <p className="text-xs text-muted-foreground"><strong>Inspection complete / Diagnosis complete</strong> appears in both flows: on <strong>Pause</strong> it means "awaiting next decision" (work may continue now); on <strong>Stop</strong> it means "sell / schedule / handle later." Full FOH Workboard details are covered in the FOH section.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'parking-lot',
          title: 'Parking Lot & Rollover',
          content: (
            <div className="space-y-4">
              <p>Manage tickets that extend beyond a single day:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Parking Lot View:</strong> See all multi-day tickets and their current status</li>
                <li><strong>Automatic Rollover:</strong> Non completed tickets automatically rollover to the next days Parking Lot or technicians schedule</li>
                <li><strong>Scheduling:</strong> Move Parking Lot tickets to any future date based on parts arrival or customer needs</li>
                <li><strong>Completion Tracking:</strong> Track which jobs are complete vs. pending on multi-day tickets</li>
              </ul>
            </div>
          )
        },
        {
          id: 'completed-tickets',
          title: 'Completed Tickets Archive',
          content: (
            <div className="space-y-4">
              <p>Access and review your shop's completed work history:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" />
                      Accessing Completed Tickets
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Navigate to <strong>Completed Tickets</strong> from the Dashboard or side menu to view all finished work.</p>
                    <p className="text-muted-foreground text-xs">Default view shows last 30 days of completed tickets.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Filtering & Search</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Date Range:</strong> Select start and end dates to narrow results</li>
                      <li><strong>Technician Filter:</strong> View work completed by specific technicians</li>
                      <li><strong>Customer Search:</strong> Search by customer name or vehicle info</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Available Data</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Each completed ticket shows:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Vehicle information and customer details</li>
                      <li>Jobs performed with actual vs. estimated times</li>
                      <li>Technician who completed the work</li>
                      <li>Completion date and total time spent</li>
                      <li>Efficiency percentage</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Export Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Download completed ticket data for reporting or analysis. Export formats support integration with external accounting systems.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'deleted-tickets',
          title: 'Deleted Tickets & Recovery',
          content: (
            <div className="space-y-4">
              <p>Manage accidentally deleted tickets and clean up your archive:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      Soft Delete System
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Deleted tickets are not permanently removed. They move to the <strong>Deleted Tickets</strong> page where they can be reviewed, restored, or permanently archived.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Restoring Tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Navigate to <strong>Deleted Tickets</strong> from the menu</li>
                      <li>Find the ticket you want to restore</li>
                      <li>Click the <strong>Restore</strong> button (↩️ icon)</li>
                      <li>Ticket returns to active status in the Parking Lot</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Permanent Archive</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>For permanent deletion:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Click <strong>Archive</strong> on individual tickets</li>
                      <li>Or use <strong>Archive All</strong> to clear all deleted tickets</li>
                      <li>Requires typing "ARCHIVE" to confirm (prevents accidents)</li>
                    </ul>
                    <Alert className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Archived tickets cannot be recovered. Use with caution.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Real-Time Updates</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Deleted Tickets page updates in real-time. When tickets are deleted or restored elsewhere, the list automatically refreshes.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'optiboard',
          title: 'OptiBoard',
          content: (
            <div className="space-y-4">
              <p>OptiBoard is your full ticket lifecycle management view, providing a comprehensive list of every ticket across all statuses.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Status Tabs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Filter tickets by lifecycle stage:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>All:</strong> Every ticket across all statuses</li>
                      <li><strong>Estimates:</strong> Tickets in estimate/quote stage</li>
                      <li><strong>Scheduled:</strong> Tickets with future scheduled dates</li>
                      <li><strong>WIP (Work in Progress):</strong> Currently active tickets</li>
                      <li><strong>Completed:</strong> Finished tickets awaiting pickup</li>
                      <li><strong>Picked Up:</strong> Vehicles returned to customers</li>
                      <li><strong>Deleted:</strong> Soft-deleted tickets (recoverable)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Search & Filter</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Search across multiple fields simultaneously:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Customer name</li>
                      <li>Phone number</li>
                      <li>VIN (full or partial)</li>
                      <li>Vehicle year, make, model</li>
                      <li>Ticket number</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Real-Time Updates</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>OptiBoard uses real-time subscriptions to automatically reflect ticket changes made anywhere in the system. No manual refresh needed.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Estimate Creation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Create estimates directly from OptiBoard. Estimates follow the same ticket creation flow but start in estimate status, allowing you to build quotes before converting to active tickets.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'customer-directory',
          title: 'Customer Directory',
          content: (
            <div className="space-y-4">
              <p>The Customer Directory (<strong>/customers</strong>) provides a searchable list of all shop customers.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Customer List
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Browse and search your entire customer database:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Name:</strong> Customer full name</li>
                      <li><strong>Phone:</strong> Primary phone number</li>
                      <li><strong>Email:</strong> Email address</li>
                      <li><strong>Vehicle Count:</strong> Number of vehicles on file</li>
                    </ul>
                    <p className="mt-2">Click any customer row to navigate to their individual profile page.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Search</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Search by name, phone, email, or VIN to quickly find customers. Results update as you type with fuzzy matching support.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'customer-profile',
          title: 'Customer Profile',
          content: (
            <div className="space-y-4">
              <p>Individual customer pages (<strong>/customers/:id</strong>) provide a comprehensive view of each customer's history with your shop.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCircle className="h-4 w-4" />
                      Profile Sections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Contact Info:</strong> Name, phone, email, address, communication preferences (SMS/email opt-in)</li>
                      <li><strong>Vehicles:</strong> All vehicles on file with year, make, model, VIN, and license plate</li>
                      <li><strong>Ticket History:</strong> Complete service history across all visits</li>
                      <li><strong>Communication Records:</strong> SMS and message history with the customer</li>
                      <li><strong>Service Recommendations:</strong> Pending and completed recommendations</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'customer-inbox',
          title: 'Customer Inbox',
          content: (
            <div className="space-y-4">
              <p>The Customer Inbox (<strong>/customer-inbox</strong>) manages unattached SMS threads from unknown phone numbers.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Inbox className="h-4 w-4" />
                      Unattached Threads
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When SMS messages arrive from phone numbers not linked to a customer, they appear in the Customer Inbox as unattached threads.</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Thread List:</strong> Shows phone number, message count, and last message timestamp</li>
                      <li><strong>Message Timeline:</strong> View the full conversation with event types</li>
                      <li><strong>Link to Customer:</strong> Attach the thread to an existing customer record</li>
                      <li><strong>Link to Ticket:</strong> Associate the thread with an active ticket</li>
                      <li><strong>Create Customer:</strong> Create a new customer record from the thread</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'workflow-panel',
          title: 'Ticket Workflow Panel',
          content: (
            <div className="space-y-4">
              <p>The Workflow Panel is a sidebar tab within each ticket that centralizes all workflow-related activities.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4" />
                      Workflow Sections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold">Events Timeline</p>
                      <p className="text-muted-foreground text-xs">Filtered log of ticket events: status changes, assignments, timer actions, and more.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Complaints</p>
                      <p className="text-muted-foreground text-xs">Add and edit customer complaints. <Badge variant="outline" className="text-xs">Service Writer+</Badge></p>
                    </div>
                    <div>
                      <p className="font-semibold">Notes</p>
                      <p className="text-muted-foreground text-xs">Add and edit workflow notes visible to all team members. All roles can contribute.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Inspections</p>
                      <p className="text-muted-foreground text-xs">Start, save inspection items, and complete inspections. <Badge variant="outline" className="text-xs">Technician+</Badge></p>
                    </div>
                    <div>
                      <p className="font-semibold">Diagnostics</p>
                      <p className="text-muted-foreground text-xs">Create, update, start, and complete diagnostics with test management. <Badge variant="outline" className="text-xs">Technician+</Badge></p>
                    </div>
                    <div>
                      <p className="font-semibold">Communications</p>
                      <p className="text-muted-foreground text-xs">View communication history associated with this ticket.</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quick Status Chips</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The panel header shows quick-reference badges for ticket status, assigned technician, and bay assignment.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'service-info-acknowledgment',
          title: 'Service Info Acknowledgment',
          content: (
            <div className="space-y-4">
              <p>A pre-start compliance prompt that appears before a technician begins work on a ticket.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Compass className="h-4 w-4" />
                      Purpose
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Ensures technicians have consulted relevant service information (OEM procedures, TSBs, specifications) before starting work. Supports OCIS (Occupational Competency and Information Standards) compliance.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How It Works</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ol className="list-decimal list-inside ml-2 space-y-1">
                      <li>When starting a ticket timer, the prompt appears: <em>"Was service information consulted for this work?"</em></li>
                      <li><strong>Yes:</strong> Optionally select information sources (OEM, Alldata, Mitchell, Identifix, TSB, Other) and add a note</li>
                      <li><strong>Not yet:</strong> Timer starts but acknowledgment is logged as not consulted</li>
                    </ol>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Audit Trail</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Every acknowledgment (or lack thereof) is logged as a ticket event for compliance auditing. Records include sources consulted, any notes, and timestamp.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'foh-queue',
          title: 'FOH Workboard (Front Office)',
          featureGate: 'custodyHandoff',
          // Front-office surface — technicians work handoffs from the ticket
          // card (the accept-back guidance below), not this page.
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN', 'SERVICE_WRITER'],
          productArea: 'FOH / Custody',
          status: 'live',
          tierLabel: 'CORE',
          content: (
            <div className="space-y-4">
              <p>The <strong>FOH Workboard</strong> is the front office's home screen: every repair order you are responsible for, what needs attention next, and what you are waiting on. It is also where you pick up tickets that technicians hand off, and hand them back when the front-office step is done. Open it from the main navigation (<strong>FOH Workboard</strong>). It's available to <strong>service writers/advisors, managers, owners, and org admins</strong> — technicians work handoffs from the ticket card instead (see Technician accept-back below).</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Lanes and your repair orders</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Each repair order appears once, in one lane, and moves on its own as the shop's facts change — there is nothing to drag:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Needs Attention</strong> — a handoff to accept, or a ticket the front office is holding.</li>
                      <li><strong>Waiting</strong> — waiting on the customer's approval or on parts.</li>
                      <li><strong>With the Shop</strong> — being worked, paused, or waiting for a technician.</li>
                      <li><strong>Closeout</strong> — work is finished; invoice and hand the vehicle back.</li>
                      <li><strong>Upcoming</strong> — scheduled for a later day.</li>
                      <li><strong>Done Today</strong> — picked up today.</li>
                    </ul>
                    <p>Each repair order has a <strong>responsible advisor</strong>. Use <strong>My Tickets</strong>, an advisor's name, <strong>All</strong>, or <strong>Unassigned</strong> to choose whose work you see. A multi-day job is one repair order, shown with today's work.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How a ticket reaches the front office</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>A technician sends a ticket to the front office by choosing <strong>"Also kick to FOH"</strong> when pausing or stopping the timer (see the Pause/Stop + Kick to FOH sections). Each ticket carries a <strong>handoff reason</strong> so the advisor knows what's needed.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Handoff states</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Waiting for advisor</strong> — kicked to the front office, not yet picked up.</li>
                      <li><strong>With advisor</strong> — an advisor has accepted it and is working the front-office step.</li>
                      <li><strong>Waiting for technician</strong> — the advisor sent it back; the technician hasn't accepted it yet.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Front-office actions</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Accept ticket</strong> — pick up a "waiting for advisor" handoff (moves it to "with advisor"). If the repair order has no advisor yet, you become its advisor; if it already has one, it stays theirs.</li>
                      <li><strong>Claim / Reassign</strong> — take an unassigned repair order, or change its responsible advisor. Handing a ticket to the technician and back never changes who is responsible for it.</li>
                      <li><strong>Back to technician</strong> — when the front-office step is done (or the tech needs to act), send it back and pick a reason (e.g. parts arrived, additional work approved, more information needed).</li>
                    </ul>
                    <p className="mt-2">Each card shows the <strong>handoff reason</strong>, who sent it and their note, so you can triage at a glance. Tap a card to open the ticket.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Technician accept-back</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Technicians don't use the FOH Workboard. When a ticket is sent back, its <strong>custody badge on the ticket card</strong> turns red ("Pending Tech Accept"). The technician clicks the badge to <strong>accept the ticket back</strong> and resume work — no reason prompt required.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Aging & urgency</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Tickets waiting for an advisor are highlighted as they age — a <strong>warning</strong> after about 5 minutes and <strong>urgent</strong> after about 10 minutes — so the front office can keep wait times down.</p>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Handoffs are tracked on each ticket's custody record and on the FOH Workboard. They are not yet shown in the ticket Activity timeline.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'pause-kick-foh',
          title: 'Pause + Kick to FOH (tech-blocking handoff)',
          featureGate: 'custodyHandoff',
          productArea: 'FOH / Custody',
          status: 'live',
          tierLabel: 'CORE',
          content: (
            <div className="space-y-4">
              <p>Use <strong>Pause + Kick to FOH</strong> when you're <strong>not done</strong>, but the front office must act before you can continue. This is a <strong>tech-blocking handoff</strong>.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How it works</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Open the <strong>Pause</strong> dialog and pick your pause reason as usual.</li>
                      <li>Check <strong>"Also kick to FOH"</strong> and choose why the front office is needed.</li>
                      <li>The <strong>timer pause happens first</strong> and is the primary action; the FOH handoff is optional and secondary.</li>
                      <li>If the FOH handoff fails, your <strong>pause is still saved</strong> — the timer action is never rolled back.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Reasons</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Needs estimate</li>
                      <li>Needs customer approval</li>
                      <li>Needs parts or pricing</li>
                      <li>Waiting on parts</li>
                      <li>Advisor question</li>
                      <li>Diagnostic authorization needed</li>
                      <li>Inspection complete — awaiting next decision</li>
                      <li>Diagnosis complete — awaiting next decision</li>
                      <li>Vehicle blocked</li>
                      <li>Other</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2"><strong>"Awaiting next decision"</strong> means more work is expected once the front office or customer decides — so the job can continue shortly. (Compare with the Stop version, which means sell or schedule later.)</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'stop-kick-foh',
          title: 'Stop + Kick to FOH (closeout / next-step handoff)',
          featureGate: 'custodyHandoff',
          productArea: 'FOH / Custody',
          status: 'live',
          tierLabel: 'CORE',
          content: (
            <div className="space-y-4">
              <p>Use <strong>Stop + Kick to FOH</strong> when your currently authorized/scheduled work is <strong>complete</strong> and the front office owns the next step. This is a <strong>closeout / next-step handoff</strong>.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How it works</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Open the <strong>Stop &amp; Complete</strong> dialog as usual — the ticket completes and moves to the Parking Lot.</li>
                      <li>Check <strong>"Also kick to FOH"</strong> and choose what the front office should do next.</li>
                      <li>The <strong>stop/complete happens first</strong> and is the primary action; the FOH handoff is optional and secondary.</li>
                      <li>If the FOH handoff fails, your <strong>stop/complete is still saved</strong> — completion and parking behavior are never rolled back.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Reasons</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Inspection complete — sell or schedule</li>
                      <li>Diagnosis complete — sell or schedule</li>
                      <li>Repair complete</li>
                      <li>Ready for customer contact</li>
                      <li>Ready for invoice</li>
                      <li>Ready for pickup</li>
                      <li>QC needed</li>
                      <li>Other</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2"><strong>"Sell or schedule"</strong> means the authorized work is finished and any further repairs should be sold or scheduled later — distinct from the Pause version, where work continues now.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'optibay-connect',
          title: 'OptiBay Connect',
          featureGate: 'optibayConnect',
          productArea: 'Talent / Hiring',
          status: 'live',
          tierLabel: 'CORE',
          content: (
            <div className="space-y-4">
              <p>OptiBay Connect is a talent and hiring marketplace that links shops with automotive professionals. Open it from <strong>Connect</strong> in the navigation. What you see depends on whether you're using it as an individual (Passport) or as a shop.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">For individuals (Passport)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Control your visibility to recruiting shops.</li>
                      <li>Browse recruiting shops and workforce opportunities.</li>
                      <li>Review connection requests and job offers you've received.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">For shops (hiring)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Set your hiring/recruiting controls.</li>
                      <li>Browse the talent directory and post workforce listings.</li>
                      <li>Manage your connection inbox, build and send offers, and track offers sent.</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'scheduling',
      title: 'Scheduling & Time Management',
      icon: Calendar,
      subsections: [
        {
          id: 'break-scheduling',
          title: 'Break Scheduling',
          content: (
            <div className="space-y-4">
              <p>Configure breaks to ensure accurate completion time estimates:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Recurring Breaks:</strong> Set up daily lunch breaks (e.g., 12:00 PM - 1:00 PM)</li>
                <li><strong>Custom Breaks:</strong> Add one-time breaks for meetings, training, etc.</li>
                <li><strong>OptiFinish Calculation:</strong> System automatically adds break time to completion estimates</li>
                <li><strong>Visual Indicators:</strong> Break periods shown on ticket cards</li>
              </ul>
            </div>
          )
        },
        {
          id: 'time-clock',
          title: 'Time Clock System',
          content: (
            <div className="space-y-4">
              <p>Track technician attendance and hours. Can be turned on and off with no effect to OptiBay's performance:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Clock In/Out</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Technicians can clock in and out using the Time Clock button in the interface. All clock events are logged with timestamps.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Schedule Management</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Managers and Service Writers can view and edit technician schedules, including planned shifts and actual worked hours.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Time Clock Reports</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Access detailed time clock reports in <strong>Shop Profile → Time Clock</strong>:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Summary View:</strong> Total hours per technician for selected period</li>
                      <li><strong>Detailed View:</strong> Individual clock in/out events with timestamps</li>
                      <li><strong>Schedule Compliance:</strong> Compare scheduled vs. actual hours</li>
                      <li><strong>Export CSV:</strong> Download data for payroll processing</li>
                      <li><strong>Date Range Filter:</strong> Select custom date ranges for reporting</li>
                      <li><strong>Technician Filter:</strong> View reports for specific team members</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Manual Entry</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Managers can add manual time entries for missed clock events or adjustments. Manual entries are flagged in reports for audit purposes.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'payroll-reports',
          title: 'Payroll & Productivity Reports',
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN'],
          productArea: 'Payroll',
          content: (
            <div className="space-y-4">
              <p>Manager-level reporting for tracking staff clock hours and technician productivity for payroll processing:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Accessing Payroll Reports
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Shop Profile → Payroll</strong> tab. Only managers have access to payroll reports.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Date Range & Filters</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Date Presets:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Today, Yesterday, Week to Date (Monday start), Month to Date, Year to Date</li>
                      <li>Custom date range picker</li>
                    </ul>
                    <p className="mt-2"><strong>Employee Filters:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>All Staff, Technicians Only, Non-Tech Staff Only</li>
                      <li>Individual employee selection</li>
                    </ul>
                    <p className="mt-2"><strong>Compare to Last Year:</strong> Toggle to show year-over-year comparison</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Produced Hours Modes</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Booked Mode:</strong> Hours calculated from completed tickets (tickets.completed_at)</li>
                      <li><strong>Wrench Mode:</strong> Hours calculated from actual timer sessions (ticket_timer_sessions.stopped_at)</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Toggle between modes based on your shop's payroll methodology.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Summary Cards</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Total Clock Hours:</strong> Sum of all clock-in/clock-out time</li>
                      <li><strong>Produced Hours:</strong> Billable work hours (Booked or Wrench)</li>
                      <li><strong>Efficiency %:</strong> Produced / Clock hours (technicians only)</li>
                      <li><strong>After-Hours:</strong> Time worked outside scheduled shifts</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Employee Detail Drawer</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Click any employee row to open a detailed breakdown showing individual clock entries, job completions, and efficiency metrics for the selected period.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Printer className="h-4 w-4" />
                      Export Options
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Generic CSV:</strong> Standard comma-separated format for any payroll system</li>
                      <li><strong>QuickBooks Desktop (IIF):</strong> TIMEACT format for QuickBooks Desktop import</li>
                      <li><strong>QuickBooks Online (Timesheet CSV):</strong> Timesheet format for QBO import</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Mapping tooltips explain how fields translate to each format.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Review Flags</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Entries flagged as "Unreviewed" indicate clock-outs that haven't been approved by a manager. Review and approve before exporting to payroll.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'smart-assignment',
          title: 'OptiPlace & OptiAssign Intelligence',
          content: (
            <div className="space-y-4">
              <p>Intelligent job assignment that learns and improves over time:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Core Assignment Logic</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Skill Matching:</strong> Matches job categories to technician proficiency</li>
                      <li><strong>Capacity Awareness:</strong> Respects bay capacity and workload limits</li>
                      <li><strong>Break Integration:</strong> Accounts for scheduled breaks</li>
                      <li><strong>Priority Handling:</strong> Waiter tickets get priority over Drop Off tickets</li>
                      <li><strong>Manual Override:</strong> Drag and drop to manually reassign</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      How Completed Tickets Influence Future Assignments
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Every completed ticket makes the system smarter:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Adds to technician's historical performance record</li>
                      <li>System learns job category preferences and speeds</li>
                      <li>Builds affinity scores between techs and job types</li>
                      <li>Tracks comeback work for future prioritization</li>
                      <li>Adjusts efficiency and proficiency ratings automatically</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">OptiPlace Ranking Algorithm</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The system calculates a score for each technician:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Base Score:</strong> Technician skill tier (0-5 scale)</li>
                      <li><strong>Proficiency Multiplier:</strong> Match between job category and tech's proficiency</li>
                      <li><strong>Efficiency Adjustment:</strong> Historical completion speed</li>
                      <li><strong>Workload Balance:</strong> Current capacity and fairness</li>
                      <li><strong>Comeback Bonus:</strong> Boosts score if tech previously worked on vehicle</li>
                      <li><strong>Willingness Gates:</strong> Respects shop culture reassignment settings</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Real-Time Optimization</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Rankings update automatically as:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Tickets are completed</li>
                      <li>New jobs are added to tickets</li>
                      <li>Technician proficiencies are updated</li>
                      <li>Shop culture settings change</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'due-out-mitigation',
          title: 'Due Out Mitigation',
          content: (
            <div className="space-y-4">
              <p>When a ticket's predicted finish time exceeds its due-out time, the system offers mitigation options:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      When It Appears
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Due Out Mitigation modal appears when OptiAssign detects that the selected placement would cause the ticket to finish after the promised due-out time. This prevents accidentally over-promising to customers.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Mitigation Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-2">
                      <li><strong>Accept Delay:</strong> Proceed with placement and update the customer's expected completion time</li>
                      <li><strong>Find Earlier Slot:</strong> Search for an earlier available time slot with any technician</li>
                      <li><strong>Split Ticket:</strong> Split work across multiple technicians to finish faster</li>
                      <li><strong>Move to Parking Lot:</strong> Hold the ticket for manual scheduling later</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Configure due-out tolerance in <strong>Shop Profile → Shop Culture</strong>. Set how many minutes past due-out is acceptable before triggering mitigation.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'waiter-bump-warning',
          title: 'Waiter Bump Warning',
          content: (
            <div className="space-y-4">
              <p>Protect waiter appointments from being pushed back by other work:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      When It Appears
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Waiter Bump Warning appears when a scheduled waiter appointment would be pushed later than its promised time due to preceding work running over. The warning triggers when the delay exceeds your configured waiter flexibility window.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Warning Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-2">
                      <li><strong>Reassign Waiter:</strong> Move the waiter appointment to a different technician who can start on time</li>
                      <li><strong>Reassign Blocking Tickets:</strong> Move the tickets that are causing the delay to another technician</li>
                      <li><strong>Accept Delay:</strong> Proceed with the delayed start and update customer expectations</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Waiter Flexibility Setting</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Configure <strong>Waiter Time Flexibility</strong> in Shop Profile → Shop Culture. This slider (0-30 minutes) sets how much delay is acceptable before triggering the warning. Set to 0 for strict appointment times.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'learning-system',
          title: 'Learning System & Proficiency Evolution',
          content: (
            <div className="space-y-4">
              <p>OptiBay Core learns and adapts to your team's actual performance:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      How the System Learns
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Tracks actual completion times vs. predicted times for each technician</li>
                      <li>Monitors which job types each tech completes successfully</li>
                      <li>Records historical affinity between techs and specific job categories</li>
                      <li>Analyzes break adherence and timing patterns</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Automatic Efficiency Adjustments</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Efficiency Slider:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>What it tracks:</strong> Does tech finish faster or slower than standard times?</li>
                      <li><strong>How it updates:</strong> Efficiency percentage automatically refined over time</li>
                      <li><strong>Impact:</strong> More accurate OptiFinish predictions</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Automatic Proficiency Adjustments</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Proficiency Sliders (per job category):</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>What it tracks:</strong> Success rate and completion speed for each job type</li>
                      <li><strong>How it updates:</strong> Proficiency levels increase as tech completes more jobs in category</li>
                      <li><strong>Impact:</strong> Better job-to-tech matching</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Manager Override</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Manual adjustments to efficiency and proficiency sliders always take precedence over automatic learning. Use this to account for training, certifications, or other factors the system can't detect.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Continuous Improvement</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The more the system is used, the more accurate predictions become. After 30-60 days of consistent use, most shops see significant improvements in schedule accuracy.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'communication',
      title: 'Communication & Reminders',
      icon: Bell,
      subsections: [
        {
          id: 'business-day-mode',
          title: 'Business Day Mode',
          content: (
            <div className="space-y-4">
              <p>Intelligent scheduling that respects your shop's operating hours:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Automatic after-hours detection</li>
                <li>Next business day calculations</li>
                <li>Holiday and weekend handling</li>
                <li>Custom shop hours configuration</li>
              </ul>
            </div>
          )
        },
        {
          id: 'reminder-setup',
          title: 'Appointment Reminders',
          content: (
            <div className="space-y-4">
              <p>Reduce no-shows with automated appointment reminders:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Reminder Types</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>1 Week Before:</strong> Initial appointment confirmation</p>
                    <p><strong>24 Hours Before:</strong> Final reminder with confirmation request</p>
                    <p><strong>Day Of:</strong> Morning reminder on appointment day</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Go to Shop Profile → Reminder Settings to enable/disable each reminder type and customize message templates.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'email-sms',
          title: 'Email & SMS Configuration',
          content: (
            <div className="space-y-4">
              <p>Set up your communication channels:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Setup
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Email reminders are sent automatically using your shop's configured email settings. No additional setup required.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      SMS via Twilio
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>To enable SMS reminders (requires shop's own Twilio account):</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Create your shop's own Twilio account</li>
                      <li>Get a Twilio phone number</li>
                      <li>Configure webhook in Twilio console</li>
                      <li>Enter credentials in Shop Profile settings</li>
                    </ol>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'twilio-webhook',
          title: 'Twilio Webhook Setup',
          content: (
            <div className="space-y-4">
              <p>Enable two-way SMS communication for appointment confirmations:</p>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>Log into your Twilio console</li>
                <li>Navigate to Phone Numbers → Active Numbers</li>
                <li>Select your SMS-enabled number</li>
                <li>Scroll to "Messaging" section</li>
                <li>Set "A MESSAGE COMES IN" webhook URL to your shop's webhook endpoint</li>
                <li>Set method to "HTTP POST"</li>
                <li>Save configuration</li>
              </ol>
              <div className="bg-muted p-4 rounded-lg mt-4">
                <p className="text-sm"><strong>Note:</strong> Contact support for your webhook URL if you need assistance.</p>
              </div>
            </div>
          )
        },
        {
          id: 'confirmations',
          title: 'Two-Way SMS Confirmations',
          content: (
            <div className="space-y-4">
              <p>Customers can confirm or cancel appointments via SMS reply:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Confirm:</strong> Customer replies "YES" or "CONFIRM" to confirm appointment</li>
                <li><strong>Cancel:</strong> Customer replies "NO" or "CANCEL" to cancel</li>
                <li><strong>Status Updates:</strong> Confirmation badges appear on tickets showing response status</li>
                <li><strong>Analytics:</strong> Track confirmation rates in the Analytics dashboard</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reporting',
      icon: TrendingUp,
      subsections: [
        {
          id: 'reminder-analytics',
          title: 'Reminder Analytics',
          content: (
            <div className="space-y-4">
              <p>Track the effectiveness of your reminder system:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Delivery Rates:</strong> See how many reminders were successfully sent vs. failed</li>
                <li><strong>Response Rates:</strong> Track customer confirmation and cancellation percentages</li>
                <li><strong>Channel Performance:</strong> Compare SMS vs. Email effectiveness</li>
                <li><strong>Time Analysis:</strong> View response patterns by time of day and day of week</li>
              </ul>
            </div>
          )
        },
        {
          id: 'performance-metrics',
          title: 'Performance Metrics',
          content: (
            <div className="space-y-4">
              <p>Monitor shop and technician performance:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Ticket Throughput:</strong> Track completed tickets per day/week/month</li>
                <li><strong>OptiFinish Accuracy:</strong> Compare estimated vs. actual completion times</li>
                <li><strong>Technician Utilization:</strong> See how efficiently technicians are scheduled</li>
              </ul>
            </div>
          )
        },
        {
          id: 'team-productivity',
          title: 'Team Productivity',
          content: (
            <div className="space-y-4">
              <p>Identify opportunities for improvement:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Job Timing:</strong> Compare estimated vs. actual time for different job types</li>
                <li><strong>Skill Gaps:</strong> Identify areas where additional training may be needed</li>
                <li><strong>Bay Efficiency:</strong> See which bays are most/least productive</li>
                <li><strong>Peak Hours:</strong> Understand your busiest times for better staffing</li>
              </ul>
            </div>
          )
        },
        {
          id: 'analytics-dashboard',
          title: 'Analytics Dashboard',
          content: (
            <div className="space-y-4">
              <p>The Analytics Dashboard provides comprehensive performance tracking and insights across eight dedicated tabs:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Tab 1: Technicians
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Monitor individual technician performance with key metrics:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Efficiency Rating:</strong> Percentage of jobs completed on or under estimated time</li>
                      <li><strong>Total Jobs Completed:</strong> Count of finished jobs in selected period</li>
                      <li><strong>Avg. Completion Time:</strong> Average actual minutes spent per job</li>
                      <li><strong>Comeback Rate:</strong> Percentage of warranty/redo tickets</li>
                      <li><strong>Diagnostic Accuracy:</strong> Percentage of confirmed diagnoses</li>
                    </ul>
                    <p className="mt-2">Performance cards display for each active technician with color-coded efficiency indicators (green ≥80%, yellow 60-79%, red &lt;60%).</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Tab 2: Advisors</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Track service writer and manager activity (Manager role only):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Tickets Created:</strong> Number of new tickets entered</li>
                      <li><strong>Tickets Edited:</strong> Count of modifications made</li>
                      <li><strong>Waiter Conversions:</strong> Waiters converted to drop-offs</li>
                      <li><strong>Scheduling Overrides:</strong> Manual assignment changes</li>
                      <li><strong>Jobs Approved/Declined:</strong> Customer authorization tracking</li>
                    </ul>
                    <p className="mt-2">Daily metrics aggregated per advisor with date range filtering. Click an advisor name to open their Advisor Passport (included in OptiBay Core).</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Tab 3: Jobs</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Analyze job-level statistics and trends:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Job Category Breakdown:</strong> Volume by category (Brakes, Engine, Diagnostics, etc.)</li>
                      <li><strong>Completion Time Analysis:</strong> Estimated vs. actual time variance</li>
                      <li><strong>Most/Least Profitable Jobs:</strong> Time efficiency by job type</li>
                      <li><strong>Skills Distribution:</strong> Job complexity levels serviced</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <LineChart className="h-4 w-4" />
                      Tab 4: Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Historical trend charts showing patterns over time:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Technician Trend Chart:</strong> Efficiency and completion rates per tech over time</li>
                      <li><strong>Job Performance Trends:</strong> Category-level time accuracy trends</li>
                      <li><strong>Shop Metrics Trends:</strong> Capacity utilization, backlog size, rollover rates, waiter promise accuracy</li>
                      <li><strong>Waiter Promise Widget:</strong> On-time start percentage (within ±5min tolerance)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Tab 5: Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Deep-dive performance analytics with multiple breakdown options:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Job Category View:</strong> Performance grouped by job category (Brakes, Engine, etc.)</li>
                      <li><strong>Make/Model View:</strong> Performance by vehicle make and model</li>
                      <li><strong>Year Group View:</strong> Performance by vehicle year (5-year buckets)</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Helps identify patterns in where technicians excel or struggle.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <PauseCircle className="h-4 w-4" />
                      Tab 6: Pause Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Analyze where workflow time is lost to pauses:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Shop Overview:</strong> Total pause time, average per ticket, % of workflow paused</li>
                      <li><strong>By Pause Reason:</strong> Breakdown by reason (Parts Wait, Lunch, Customer Auth, etc.)</li>
                      <li><strong>By Job Category:</strong> Which job types have most pause time</li>
                      <li><strong>By Make/Model:</strong> Vehicle-specific pause patterns</li>
                      <li><strong>By Year Group:</strong> Pause time by vehicle age</li>
                      <li><strong>By Technician:</strong> Individual technician pause patterns</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Respects date filters and uses aggregated data from pause sessions.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gauge className="h-4 w-4" />
                      Tab 7: Shop Health <Badge variant="outline" className="ml-2 text-xs">Manager Only</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>High-level shop health metrics for managers:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>OBLI Trend:</strong> OptiBay Load Index over time</li>
                      <li><strong>Workload Distribution:</strong> Balance across technicians</li>
                      <li><strong>Promise Accuracy:</strong> Waiter promise vs actual completion</li>
                      <li><strong>Rollover Rate:</strong> Percentage of tickets rolling to next day</li>
                      <li><strong>Capacity Utilization:</strong> How efficiently shop capacity is used</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Tab 8: Alerts (Manager Only)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Configure performance alerts and view notification history:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Alert Thresholds:</strong> Set metric-based triggers (e.g., "Alert when tech efficiency drops below 75%")</li>
                      <li><strong>Delivery Channels:</strong> In-app notifications with optional email/SMS (future)</li>
                      <li><strong>Alert History:</strong> View past alerts with timestamp and metric values</li>
                      <li><strong>Role Targeting:</strong> Choose which roles receive each alert type</li>
                    </ul>
                    <p className="mt-2">Alerts check metrics hourly via background job and notify designated users when thresholds are crossed.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing the Dashboard</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Analytics</strong> from the main navigation menu. All metrics refresh in real-time as tickets are completed and jobs are logged.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'advisor-performance',
          title: 'Advisor Performance (FOH Analytics)',
          featureGate: 'advisorTimingAnalytics',
          // Matches the in-app Analytics tab gate: management-only.
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN'],
          productArea: 'Analytics',
          status: 'live',
          tierLabel: 'CORE',
          content: (
            <div className="space-y-4">
              <p>The <strong>Advisor Performance</strong> tab on the Analytics page tells the story of your shop's front-office flow — how quickly advisors pick up tickets, how long they hold them, and how handoffs break down. It is visible to <strong>owners, managers, and org admins</strong>, and is <strong>hidden from technicians and service writers</strong>.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Scope &amp; freshness</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Shop-scoped</strong> for now (one shop at a time).</li>
                      <li>Covers the <strong>last 30 days</strong>.</li>
                      <li><strong>Not real-time</strong> — refresh the tab to see newly recorded handoffs.</li>
                      <li>Built from <strong>real custody and advisor-action events</strong>, not estimates or projections.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Shop overview</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Live FOH queue count, plus waiting-for-advisor, with-advisor, and waiting-for-technician counts.</li>
                      <li>Tickets accepted, kicked to FOH, and kicked back to technician.</li>
                      <li>Average advisor accept lag (how long tickets wait to be picked up).</li>
                      <li>Average FOH custody time (how long advisors hold tickets).</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Handoff-type breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Tech-blocking handoffs</strong> (pause + kick to FOH).</li>
                      <li><strong>Closeout / next-step handoffs</strong> (stop + kick to FOH).</li>
                      <li><strong>Manual handoffs</strong>.</li>
                      <li>A <strong>reason breakdown</strong> showing why tickets were kicked to the front office.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Per-advisor table</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>A row per advisor with tickets accepted, average and percentile accept lag, average FOH custody time, kicked-back counts, and tech-blocking vs closeout handoffs handled. Use it to spot bottlenecks and coach for faster, more consistent front-office response.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Advanced Features',
      icon: Settings,
      subsections: [
        {
          id: 'shop-culture',
          title: 'Shop Culture Configuration',
          content: (
            <div className="space-y-4">
              <p>Fine-tune the scheduling system to match your shop's preferences:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Purpose</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Shop Culture settings let you customize how the system balances workload, assigns jobs, and handles edge cases. Find these in <strong>Shop Profile → Culture Tab</strong>.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Available Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold">Willingness to Reassign (0-100%)</p>
                      <p className="text-muted-foreground">Controls how flexible system is with reassigning work between technicians. Higher = more willing to move jobs for optimization.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Skill Tier Gap for Splitting (0-10)</p>
                      <p className="text-muted-foreground">Minimum skill level difference required before system suggests splitting complex tickets across techs.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Workload Balance Preference (0-1)</p>
                      <p className="text-muted-foreground">Weight between balancing workload evenly vs. assigning to most skilled specialist.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Comeback Nudge (0-100%)</p>
                      <p className="text-muted-foreground">Priority boost for comeback/warranty work to original technician. Higher = stronger preference.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Willingness to Roll Past EOD (0-60 min)</p>
                      <p className="text-muted-foreground">How far past end-of-day system will schedule tickets. Helps with urgent work but may create overtime.</p>
                    </div>
                    <div>
                      <p className="font-semibold">Present Vehicle Priority (0-120 min)</p>
                      <p className="text-muted-foreground">Time boost given to vehicles already at shop vs. future appointments. Higher = drop-offs scheduled sooner.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'weather-change-alerts',
          title: 'Weather Change Alerts',
          content: (
            <div className="space-y-4">
              <p>Reactive monitoring system that alerts you when forecast changes affect scheduled outdoor work:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CloudRain className="h-4 w-4" />
                      How It Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>System scans weather forecasts every <strong>15 minutes</strong></li>
                      <li>Monitors tickets scheduled for outdoor bays within <strong>8-hour lookahead</strong></li>
                      <li>Compares current forecast against your shop's weather settings</li>
                      <li>Raises an advisory alert when conditions cross your thresholds — the work is not moved or blocked</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Alert Banner & Panel</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Banner:</strong> Compact notification at top of screen showing alert count</p>
                    <p><strong>Panel:</strong> Click banner to open side sheet listing all affected tickets with details</p>
                    <p><strong>Real-Time:</strong> Updates instantly via real-time subscriptions when alerts change</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Alert Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>For each alert, you have three options:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Dismiss:</strong> Acknowledge the alert and hide it (ticket stays in outdoor bay)</li>
                      <li><strong>Find Indoor Option:</strong> Opens OptiPlace pre-filtered for indoor bays only</li>
                      <li><strong>Reschedule:</strong> Opens reschedule modal to move to a different time/day</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Alert Reasons</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Alerts show specific reason: Rain, Snow, Storm, Cold (below temp threshold), Heat (above threshold), or Wind.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'addon-suggestions',
          title: 'Addon Suggestions',
          content: (
            <div className="space-y-4">
              <p>Intelligent recommendations for additional services based on vehicle and selected jobs:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      How It Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When creating a ticket, the system analyzes:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Selected jobs and their common companions</li>
                      <li>Vehicle year, make, and model</li>
                      <li>Known manufacturer recommendations</li>
                      <li>Your shop's historical patterns</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Suggestion Types</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Alignment:</strong> Suggested after suspension, steering, or tire work</li>
                      <li><strong>ADAS Calibration:</strong> For vehicles with driver assistance (year threshold configurable)</li>
                      <li><strong>TPMS Service:</strong> When tire work is performed</li>
                      <li><strong>Steering Angle Sensor Reset:</strong> After alignment or steering work</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Configure in <strong>Shop Profile → Addon Suggestions</strong>:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 mt-2">
                      <li>Enable/disable the feature</li>
                      <li>Set ADAS year threshold (default: 2018+)</li>
                      <li>Configure default-checked suggestions</li>
                      <li>Add custom "always prompt" or "never prompt" terms</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'optiqueue',
          title: 'OptiQueue (Public Booking)',
          featureGate: 'optiQueue',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST+ TIER</Badge>
              <p>Customer-facing online booking system that allows customers to schedule appointments directly without logging into OptiBay.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      How It Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Customers access your booking page at <code>/book/your-shop-slug</code></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Select appointment type (Maintenance, Diagnostic, Requested Work, Callback)</li>
                      <li>Enter vehicle and contact information</li>
                      <li>Choose available date and time slot</li>
                      <li>Submit booking request</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Booking Lanes</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Lane 1 - Maintenance:</strong> Standard services (oil change, tire rotation) - direct booking</li>
                      <li><strong>Lane 2 - Diagnostics:</strong> Check engine lights, noises - requires initial slot</li>
                      <li><strong>Lane 3 - Requested Work:</strong> Specific repairs customer requests - requires confirmation</li>
                      <li><strong>Lane 4 - Callback:</strong> Customer requests contact with no commitment</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Confirmation Workflow</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Lane 3 bookings require advisor confirmation:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Ticket appears with pulsing amber border and "Needs Confirmation" badge</li>
                      <li>Real-time toast notification alerts advisors</li>
                      <li>Use "Mark as Contacted" to acknowledge, or "Confirm Appointment" to finalize</li>
                      <li>Confirmed tickets become locked and ready for scheduling</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Hold & Expiration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Provisional bookings have a 10-minute hold:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Time slot is reserved during customer checkout</li>
                      <li>Hold expires automatically if not confirmed</li>
                      <li>Atomic updates prevent double-booking race conditions</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Configure in <strong>Settings → OptiQueue</strong>:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                      <li>Enable/disable public booking</li>
                      <li>Set max waiters per day limit</li>
                      <li>Configure allowed job categories per lane</li>
                      <li>Customize confirmation requirements</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Scheduling Constraints</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Time Turner respects all shop constraints:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1 text-xs">
                      <li>Shop hours and holidays</li>
                      <li>Technician shift boundaries and breaks</li>
                      <li>Waiter EOD cutoff times</li>
                      <li>Bay availability and capacity limits</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'diagnostic-flows',
          title: 'Diagnostic Flows Wizard',
          featureGate: 'diagnosticGuidanceEnabled',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>Guided troubleshooting flowcharts that walk technicians through standardized diagnostic procedures step-by-step.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Stethoscope className="h-4 w-4" />
                      How It Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When a ticket contains diagnostic/inspection jobs:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Technician selects the customer complaint category</li>
                      <li>System presents the matching diagnostic flow</li>
                      <li>Technician follows step-by-step guided process</li>
                      <li>Captures outcomes and test results at each step</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Diagnostic Wizard Modal</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The wizard provides:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Step Navigation:</strong> Sticky controls for Confirmed/Ruled Out/Unsure/Skip</li>
                      <li><strong>Input Capture:</strong> Numeric readings, yes/no checks, text observations</li>
                      <li><strong>AI Considerations:</strong> Contextual guidance based on step and vehicle</li>
                      <li><strong>Progress Tracking:</strong> Visual progress through the diagnostic flow</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Pause/Stop Capture</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When pausing or stopping tickets with multiple diagnostic jobs:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>DiagnosticPauseCapture accordion shows all diagnostic jobs</li>
                      <li>Each job must have outcome captured or skipped</li>
                      <li>Prevents incomplete diagnostic data</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Available Complaint Categories</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Pre-built diagnostic flows include:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1 text-xs">
                      <li>Rough Idle / Misfire</li>
                      <li>No Start — No Crank</li>
                      <li>Battery Dies / No Charge</li>
                      <li>Overheating</li>
                      <li><em>Additional flows added via Admin</em></li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Training Integration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Diagnostic steps can link to relevant training artifacts, providing just-in-time learning resources when technicians need guidance on specific procedures.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'weather-integration',
          title: 'Weather Integration & Scheduling',
          content: (
            <div className="space-y-4">
              <p>Comprehensive weather-aware scheduling that protects outdoor work and optimizes bay assignments:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Thermometer className="h-4 w-4" />
                      Weather Settings Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Configure in <strong>Shop Profile → Weather Settings</strong>:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Temperature Thresholds:</strong> Min/max acceptable temps for outdoor work</li>
                      <li><strong>Precipitation Sensitivity:</strong> Rain/snow probability triggers</li>
                      <li><strong>Wind Speed Limits:</strong> Maximum wind for outdoor bay usage</li>
                      <li><strong>Storm Avoidance:</strong> Severe weather auto-blocking</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Proactive Scheduling</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>During ticket creation and assignment:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>System checks weather forecast for outdoor bay slots</li>
                      <li>Warns when weather may impact scheduled work</li>
                      <li>Suggests indoor alternatives when available</li>
                      <li>Blocks outdoor scheduling during severe conditions</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CloudRain className="h-4 w-4" />
                      Reactive Weather Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When forecast changes affect scheduled work:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>15-minute scans:</strong> System checks forecasts regularly</li>
                      <li><strong>8-hour lookahead:</strong> Monitors upcoming outdoor work</li>
                      <li><strong>Alert banner:</strong> Top-of-screen notification with count</li>
                      <li><strong>Detail panel:</strong> Lists all affected tickets with options</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Alert Resolution Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>For each weather alert:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                      <li><strong>Dismiss:</strong> Acknowledge and proceed (work stays outdoors)</li>
                      <li><strong>Find Indoor:</strong> OptiPlace filtered to indoor bays only</li>
                      <li><strong>Reschedule:</strong> Move to a different time/day</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'training-catalog-admin',
          title: 'Training Catalog Administration',
          // TDO-1: admin help — gate to admin roles.
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN'],
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">INTERNAL ADMIN</Badge>
              <p>Manage the centralized library of training resources that power the Learning Center recommendations.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookMarked className="h-4 w-4" />
                      Training Artifacts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Each training resource includes:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Type:</strong> Video, Article, Course, Simulation, Certification, Toolkit</li>
                      <li><strong>Provider:</strong> Source organization (ASE, OEM, etc.)</li>
                      <li><strong>Skill Depth:</strong> Awareness → Working Knowledge → Proficiency → Mastery</li>
                      <li><strong>Duration:</strong> Estimated completion time</li>
                      <li><strong>Domain Tags:</strong> Job categories and skill areas covered</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Job & Fitment Mapping</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Artifacts are linked to:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Job Categories:</strong> Maps to jobs_catalog for relevance scoring</li>
                      <li><strong>Vehicle Fitment:</strong> Make/model/year applicability</li>
                      <li><strong>Diagnostic Steps:</strong> Just-in-time learning during flows</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Discovery System</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>New training content is discovered via:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Configured training sources (YouTube channels, provider feeds)</li>
                      <li>AI-assisted metadata generation for categorization</li>
                      <li>Admin review and promotion to main catalog</li>
                    </ul>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        System uses metadata only - no video content or transcripts are stored (IP protection).
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing the Admin</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Settings → Training Catalog Admin</strong> (internal admin access only). Manage artifacts, sources, job mappings, and review discovered content.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'help-with-this-job',
          title: 'Help With This Job (Ticket Sidebar)',
          featureGate: 'learningCenterAdvanced',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>Context-aware training recommendations that appear in the ticket sidebar, providing just-in-time learning resources matched to the current vehicle and jobs.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Strict Matching Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The sidebar uses strict matching to ensure relevant content:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Vehicle Fitment:</strong> Make, model, and year must match artifact fitment</li>
                      <li><strong>Engine Normalization:</strong> Engine sizes are normalized (e.g., "3.5L" = "3.5 liter" = "3.5")</li>
                      <li><strong>Job Mapping:</strong> Jobs on ticket must map to artifact's job associations</li>
                      <li><strong>Video Only:</strong> Only short/long video types eligible for in-bay viewing</li>
                    </ul>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        If no engine size is set on the vehicle, no sidebar recommendations will appear. Add engine info to unlock training content.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Empty State Reasons</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When no training appears, the system explains why:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>"No engine":</strong> Vehicle missing engine size - add it to unlock content</li>
                      <li><strong>"No vehicle":</strong> Ticket has no vehicle assigned</li>
                      <li><strong>"No jobs":</strong> No approved jobs on ticket to match against</li>
                      <li><strong>"No matches":</strong> No artifacts match current vehicle + job combination</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing the Sidebar</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Open any ticket card and expand the <strong>"Help With This Job"</strong> section. Available videos matching the ticket's vehicle and jobs will be listed with thumbnails and durations.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'training-consumption',
          title: 'Training Consumption Tracking',
          featureGate: 'learningCenterAdvanced',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>The system tracks training engagement to measure completion and award soft proficiency boosts.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Session Recording
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When a technician opens training content, a consumption session records:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Context:</strong> "foundational" (recommended) vs "browse" (self-selected)</li>
                      <li><strong>Start/End Time:</strong> When content was opened and closed</li>
                      <li><strong>Expected Duration:</strong> The artifact's stated duration</li>
                      <li><strong>Actual Duration:</strong> Time spent with content open</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Completion Confidence</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The system calculates completion confidence based on time spent:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Verified:</strong> ≥80% of expected duration watched</li>
                      <li><strong>Likely:</strong> 50-79% of expected duration</li>
                      <li><strong>Unverified:</strong> 30-49% of expected duration</li>
                      <li><strong>Suspicious:</strong> &lt;30% of expected duration (no credit awarded)</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">This prevents gaming by opening/closing content quickly.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'soft-proficiency-nudges',
          title: 'Soft Proficiency & Nudges',
          featureGate: 'learningCenterAdvanced',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>Training consumption can boost technician proficiency and enable "nudges" into new job categories.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Soft Proficiency Boosts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Verified training completions award soft proficiency points:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Verified Completion:</strong> +0.5% soft proficiency in mapped categories</li>
                      <li><strong>Likely Completion:</strong> +0.3% soft proficiency</li>
                      <li><strong>Category Cap:</strong> Maximum 15% soft proficiency per category</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">Soft proficiency is stored separately from real proficiency earned through job completions.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Nudge System</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>A "nudge" allows a technician to be assigned routine jobs in a category they're learning:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Requirements:</strong> ≥10% soft proficiency AND ≥20% real proficiency</li>
                      <li><strong>Job Restrictions:</strong> Only routine jobs (skill level ≤3)</li>
                      <li><strong>Monthly Limit:</strong> 3 nudges per category per month</li>
                      <li><strong>Safety Guardrail:</strong> Complex/diagnostic work never nudge-eligible</li>
                    </ul>
                    <Alert className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Nudges are tracked in technician_soft_proficiency_nudges to prevent over-assignment to developing technicians.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'learning-affinity',
          title: 'Learning Affinity & Style Inference',
          featureGate: 'learningCenterAdvanced',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>The Divination Engine observes training consumption patterns to infer each technician's preferred learning style.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Inferred Learning Styles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The system tracks consumption preferences:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Format Preference:</strong> Video vs article vs hands-on</li>
                      <li><strong>Duration Preference:</strong> Short clips vs long-form content</li>
                      <li><strong>Completion Patterns:</strong> Time-of-day, session length habits</li>
                      <li><strong>Topic Engagement:</strong> Which domains spark deeper exploration</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Affinity Boost in Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Foundational recommendations use inferred styles for personalization:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Affinity Multiplier:</strong> 1.0x to 1.3x boost for matching preferences</li>
                      <li><strong>Schedule-Aware:</strong> Recommendations consider available time</li>
                      <li><strong>Confidence-Weighted:</strong> Higher confidence from more data points</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">A technician who prefers short videos will see those prioritized over long courses.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'training-effect-signals',
          title: 'Training Effect Signals',
          featureGate: 'learningCenter_effectiveness',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>The Learning Impact Loop measures whether training actually improves performance through outcome windows and effect signals.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Outcome Windows
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>After training engagement, the system opens observation windows:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>7-Day Window:</strong> Immediate impact on related job performance</li>
                      <li><strong>14-Day Window:</strong> Short-term retention and application</li>
                      <li><strong>30-Day Window:</strong> Sustained improvement measurement</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Effect Signal Analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Signals measure training effectiveness:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Direction:</strong> Positive, neutral, or negative impact</li>
                      <li><strong>Sample Size:</strong> Minimum 5 post-training jobs for reliable signal</li>
                      <li><strong>Domain Correlation:</strong> Which skill areas improved</li>
                      <li><strong>Weight Calculation:</strong> Capped ±30% influence on recommendation scoring</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">Training that consistently produces positive outcomes gets recommended more often.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Admin Aggregate View</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Training Catalog Admin shows aggregated effect signals per artifact, helping identify which training resources deliver measurable improvements across the shop.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'foundational-recommendations',
          title: 'Foundational Recommendations',
          featureGate: 'learningCenter_intelligence',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>The Foundational Recommendations system surfaces personalized training based on skill gaps, schedule, and learning preferences.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Recommendation Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Recommendations are generated from multiple signals:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Proficiency Gaps:</strong> Categories where technician is below shop average</li>
                      <li><strong>Diagnostic Mismatch Events:</strong> Areas where misdiagnosis patterns emerge</li>
                      <li><strong>Role-Based Needs:</strong> Manager vs technician development paths</li>
                      <li><strong>Effect Signal Priority:</strong> Training proven to produce positive outcomes</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Schedule-Aware Filtering</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Recommendations consider technician availability:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Short Breaks:</strong> Suggest quick 5-10 min content</li>
                      <li><strong>Lunch Periods:</strong> Medium-length training options</li>
                      <li><strong>End of Day:</strong> Longer courses or certifications</li>
                      <li><strong>Busy Periods:</strong> Suppress recommendations during peak load</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>View foundational recommendations in the <strong>Technician Passport → Learning Center</strong> section, or through the dedicated training recommendations panel on the dashboard.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'optitime-learning',
          title: 'OptiTime Learning System',
          featureGate: 'pensieveLearning',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>OptiTime is OptiBay's intelligent learning engine that observes, remembers, and learns from every completed job to continuously improve scheduling accuracy and technician matching.</p>
              <p className="text-xs text-muted-foreground">
                <strong>Tech OptiTime</strong> is the per-ticket scheduling/bay block shown on every ticket card; the OptiTime Learning System feeds actual completion times back into those estimates. <strong>Lifecycle Time</strong> (on the Ticket Lifecycle card) tracks the full ticket journey across all phases and is visibility-only — it does not change technician scheduling.
              </p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      How OptiTime Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>OptiTime captures detailed event data for every ticket interaction:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Timer Events:</strong> Start, pause, resume, and complete actions</li>
                      <li><strong>Job Completions:</strong> Actual vs estimated time for each job</li>
                      <li><strong>Vehicle+Job Combos:</strong> Learning technician performance on specific vehicle/job combinations</li>
                      <li><strong>Diagnostic Outcomes:</strong> Confidence levels and confirmation status</li>
                      <li><strong>Assignment Patterns:</strong> Which technicians excel at which work</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">What OptiTime Learns</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold text-xs">Time Estimates</p>
                      <p className="text-xs text-muted-foreground">Actual completion times feed back to improve future estimates. The system learns that "Tech A completes brake jobs on Honda Accords 15% faster than average."</p>
                    </div>
                    <div>
                      <p className="font-semibold text-xs">Technician Strengths</p>
                      <p className="text-xs text-muted-foreground">Identifies patterns in technician performance across job categories, vehicle types, and complexity levels.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-xs">Shop Patterns</p>
                      <p className="text-xs text-muted-foreground">Aggregate learning across the shop to identify overall trends and optimize scheduling.</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">OptiTime Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The system aggregates data into actionable statistics:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Job Stats:</strong> Per-job performance metrics by technician</li>
                      <li><strong>Efficiency Ratio:</strong> Actual vs standard time patterns</li>
                      <li><strong>Median/Avg Times:</strong> Central tendency measures for accuracy</li>
                      <li><strong>Variance Tracking:</strong> Consistency of performance</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Integration with OptiAssign</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>OptiTime data directly influences assignment decisions:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Historical affinity scores boost technicians with proven success</li>
                      <li>Learned time estimates replace catalog defaults when available</li>
                      <li>Comeback patterns reduce assignment priority</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'diagnostic-intelligence',
          title: 'Diagnostic Intelligence System',
          featureGate: 'diagnosticSmartAssignWeight',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>The Diagnostic Intelligence System provides deep insights into technician diagnostic quality, enabling data-driven coaching and smarter assignment of diagnostic work.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Stethoscope className="h-4 w-4" />
                      Diagnostic Capture Flow
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When completing diagnostic/inspection jobs, technicians capture:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Outcome:</strong> Confirmed, Partial, Inconclusive, or Misdiagnosis</li>
                      <li><strong>Confidence Level:</strong> 1-5 scale of diagnostic certainty</li>
                      <li><strong>Complaint Confirmation:</strong> Whether customer's reported issue was verified</li>
                    </ol>
                    <p className="text-muted-foreground text-xs mt-2">This data is captured via the Diagnostic Outcome Modal before ticket completion.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Diagnostic Competency Score (DCS)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>DCS is a 0-100 composite score measuring diagnostic quality:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Calibration (40%):</strong> How well confidence aligns with actual outcomes</li>
                      <li><strong>Outcome Success (35%):</strong> Rate of confirmed diagnoses</li>
                      <li><strong>Complaint Validation (15%):</strong> Customer issue confirmation rate</li>
                      <li><strong>Efficiency (10%):</strong> Diagnostic speed relative to estimates</li>
                    </ul>
                    <div className="mt-2 space-y-1">
                      <p className="font-semibold text-xs">Quality Tiers:</p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground ml-2">
                        <span>Exceptional: 85-100</span>
                        <span>Strong: 70-84</span>
                        <span>Developing: 50-69</span>
                        <span>Needs Support: 0-49</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Mismatch Detection</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The system automatically detects diagnostic quality issues:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Overconfidence:</strong> High confidence with poor outcomes</li>
                      <li><strong>Underconfidence:</strong> Low confidence with good outcomes</li>
                      <li><strong>Slow Miss:</strong> Took too long and still missed diagnosis</li>
                      <li><strong>Complaint Miss:</strong> Customer issue not verified</li>
                      <li><strong>Repeated Unconfirmed:</strong> Pattern of failing on same category</li>
                    </ul>
                    <p className="text-muted-foreground text-xs mt-2">Mismatch events are immutable for audit purposes and trigger training recommendations.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">OptiAssign Integration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>DCS influences diagnostic ticket assignment:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Bonus Only:</strong> DCS provides up to 5% assignment boost (never penalty)</li>
                      <li><strong>Diagnostic Tickets:</strong> Only applies to diagnostic/inspection work</li>
                      <li><strong>Shop Culture Setting:</strong> Enable via diagnosticWeightEnabled</li>
                    </ul>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertTitle className="text-xs">Manager Only</AlertTitle>
                      <AlertDescription className="text-xs">
                        DCS data is visible only to managers in the Technician Passport. Technicians cannot see their diagnostic scores.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'learning-center',
          title: 'Learning Center & Training System',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>The Learning Center provides integrated training resources and tracks technician development, connecting performance data to targeted learning opportunities.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Library className="h-4 w-4" />
                      Training Artifact Catalog
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Curated library of training resources:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Types:</strong> Videos, Articles, Courses, Simulations, Certifications, Toolkits</li>
                      <li><strong>Providers:</strong> ASE, OEM training, industry resources</li>
                      <li><strong>Skill Depth:</strong> Awareness, Working Knowledge, Proficiency, Mastery</li>
                      <li><strong>Domain Mapping:</strong> Linked to job categories for relevance</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Adaptive Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Training recommendations based on performance data:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Gap-Based:</strong> Targets skill categories with low proficiency</li>
                      <li><strong>Mismatch-Triggered:</strong> Responds to diagnostic quality issues</li>
                      <li><strong>Certification Prep:</strong> Suggests advancement training</li>
                      <li><strong>Priority Ranking:</strong> Most impactful training first</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Learning History Tracking</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Complete record of training engagement:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Training artifacts viewed and completed</li>
                      <li>Intent tracking (Skill Building, Refresher, Certification)</li>
                      <li>Time spent on learning activities</li>
                      <li>Completion status and dates</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Learning Center Tabs</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Access via Technician Passport → Learning Center:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Recommended:</strong> Personalized training suggestions</li>
                      <li><strong>History:</strong> Previously viewed/completed training</li>
                      <li><strong>Browse:</strong> Full catalog search (Catalyst)</li>
                      <li><strong>Profile:</strong> Learning preferences and goals (Catalyst)</li>
                      <li><strong>Impact:</strong> Training effectiveness metrics (Catalyst)</li>
                      <li><strong>Requirements:</strong> Certification tracking (Catalyst)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Training Effectiveness</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Track the impact of training on performance:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Pre/post training proficiency comparisons</li>
                      <li>Job outcome improvements after training</li>
                      <li>ROI metrics for training investment</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'advisor-passport',
          title: 'Advisor Passport',
          featureGate: 'advisorPassports',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CORE</Badge>
              <p>Intelligence metrics for service advisors (managers and service writers), providing data-driven insights into workflow quality and decision patterns. Available in OptiBay Core.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Accessing Advisor Passport
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Analytics → Advisors tab</strong> and click on any advisor name to open their Passport.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Activity Metrics Tracked</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Tickets Created:</strong> Volume of new tickets entered</li>
                      <li><strong>Tickets Edited:</strong> Modifications to existing tickets</li>
                      <li><strong>Waiter Conversions:</strong> Waiters converted to drop-offs (and reasons)</li>
                      <li><strong>Scheduling Overrides:</strong> Manual assignment changes vs. OptiPlace suggestions</li>
                      <li><strong>Jobs Approved/Declined:</strong> Customer authorization patterns</li>
                      <li><strong>Drop-off vs Waiter Ratio:</strong> Ticket type distribution</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Decision Quality Indicators</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Track the quality and impact of advisor decisions:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Override Success Rate:</strong> When overrides lead to better outcomes</li>
                      <li><strong>Promise Accuracy:</strong> How often estimated times match actual</li>
                      <li><strong>Workload Balance:</strong> Distribution of tickets across technicians</li>
                      <li><strong>Authorization Efficiency:</strong> Time from ticket creation to customer approval</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Workflow Impact Analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Understand how advisor actions affect shop performance:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Rollover Contribution:</strong> Tickets that rolled due to late scheduling</li>
                      <li><strong>Technician Load Balance:</strong> How evenly work is distributed</li>
                      <li><strong>Peak Time Management:</strong> Scheduling patterns during busy periods</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Learning Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Based on decision patterns, the system surfaces coaching opportunities and best practices for improved workflow management.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'manager-passport',
          title: 'Manager Passport',
          featureGate: 'managerPassports',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST+ TIER</Badge>
              <p>High-level shop performance analytics and strategic insights for shop managers, distinct from advisor-level metrics.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      Accessing Manager Passport
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Settings → Manager Passport</strong> from the main navigation, or via the sidebar menu for manager-role users.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Shop Performance Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Daily/Weekly/Monthly Summaries:</strong> Tickets completed, revenue generated, efficiency trends</li>
                      <li><strong>Team Performance:</strong> Aggregate technician metrics and productivity comparisons</li>
                      <li><strong>Capacity Utilization:</strong> How well shop capacity is being used over time</li>
                      <li><strong>Rollover Trends:</strong> Patterns in incomplete work and their causes</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Strategic Insights</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>OBLI Trends:</strong> OptiBay Load Index patterns and anomalies</li>
                      <li><strong>Staffing Optimization:</strong> Recommendations for shift adjustments</li>
                      <li><strong>Skill Gap Analysis:</strong> Team-wide proficiency gaps and training priorities</li>
                      <li><strong>Customer Promise Accuracy:</strong> How often completion times are met</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Difference from Advisor Passport</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p><strong>Advisor Passport:</strong> Individual advisor workflow metrics (tickets created, overrides, etc.)</p>
                    <p className="mt-1"><strong>Manager Passport:</strong> Shop-wide strategic analytics, team performance, and operational health indicators.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'optiassets',
          title: 'OptiAssets (Equipment Management)',
          featureGate: 'optiAssets',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">OPTIASSETS ADD-ON</Badge>
              <p>OptiAssets is a general shop equipment registry. It tracks <strong>any</strong> shop equipment and asset — not just lifts and bays — and also calculates the labor capacity those assets enable.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      What OptiAssets Tracks
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Register and track any equipment your shop wants to manage. Examples:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Lifts & bays</strong> (automatically mirrored from your floor layout)</li>
                      <li><strong>Alignment machines, tire machines, wheel balancers</strong></li>
                      <li><strong>Scan tools, lab scopes, diagnostic equipment</strong></li>
                      <li><strong>Laptops / tablets and other computer equipment</strong></li>
                      <li><strong>A/C machines, battery equipment, fluid-service machines</strong></li>
                      <li><strong>Compressors, welders, presses, strut / spring compressors</strong></li>
                      <li><strong>Specialty tools, shop equipment, and software / subscriptions</strong></li>
                      <li><strong>Service status</strong> (in-service / out-of-service with reason + dates) and maintenance scheduling</li>
                    </ul>
                    <p className="mt-2"><strong>Category</strong> is a broad bucket for reporting and filtering. <strong>Equipment Type / Asset Type</strong> is a flexible, shop-specific free-text description (e.g. "PicoScope 4425A") that's more specific than the category.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Adding Equipment (Manual or Nameplate Scan)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Manual:</strong> Click <strong>Add Asset</strong>, give it a name, pick a category, and optionally fill in equipment type, manufacturer, model, and serial number.</p>
                    <p><strong>Nameplate scan (OCR):</strong> Snap or upload a photo of the equipment nameplate / spec plate. OptiBay reads it and <strong>prefills</strong> manufacturer, model, serial, and type for you to review and edit.</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>The scan only prefills fields — it never creates the asset for you.</li>
                      <li>You always review/edit the parsed values, then click <strong>Create Asset</strong> to save.</li>
                      <li>If the serial matches an existing asset you'll see a soft duplicate-serial warning; it's informational and does not block creation.</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Bay Enablement Attribution</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Every completed ticket attributes 100% of its book hours to the assigned bay:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Non-overlapping attribution (each ticket counts once)</li>
                      <li>Tracks labor capacity enabled per bay over time</li>
                      <li>Helps identify underutilized vs. high-demand bays</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Equipment Job Attribution</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Jobs requiring specific equipment attribute time to those assets:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Alignment work → Alignment rack</li>
                      <li>AC service → AC machine</li>
                      <li>Overlapping by design (equipment enables specific work)</li>
                    </ul>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Metrics show "labor enabled" not revenue - avoiding double-counting with bay attribution.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Opportunity Cost Tracking</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When equipment is out of service:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>System calculates "estimated lost capacity hours"</li>
                      <li>Uses 30-day rolling average of enabled hours × downtime days</li>
                      <li>Helps justify repair/replacement decisions</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing OptiAssets</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Open your <strong>Shop Profile</strong> and click the <strong>Assets</strong> button (it appears once OptiAssets is enabled) to open the OptiAssets page. Requires the OPTIASSETS add-on; managers/owners control the toggle in Shop Profile.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'multi-team-support',
          title: 'Multi-Team Support',
          featureGate: 'advancedAnalytics',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST+ TIER</Badge>
              <p>Organize technicians into specialized teams for better workload management and skill-based routing.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Team Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Create teams based on specialization:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Specialty Teams:</strong> Diesel, Euro, EV, Heavy Repair, etc.</li>
                      <li><strong>Shift Teams:</strong> Morning crew, Afternoon crew</li>
                      <li><strong>Bay Assignments:</strong> Teams can be linked to specific bay areas</li>
                      <li><strong>Flexible Membership:</strong> Technicians can belong to multiple teams</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Team-Based Routing</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>OptiAssign considers team assignments:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Diesel tickets routed to Diesel team first</li>
                      <li>Complex diagnostics to specialized diagnostic team</li>
                      <li>Fallback to general pool if specialty team unavailable</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Dayboard Team View</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Filter Dayboard by team:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Team selector dropdown in Dayboard header</li>
                      <li>Shows only technicians in selected team</li>
                      <li>"All Teams" view shows everyone</li>
                      <li>Cross-team ticket transfers with confirmation</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Manage teams in <strong>Settings → Teams</strong>. Assign technicians to teams in their individual profile settings.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'autonomous-dayboard',
          title: 'Autonomous Dayboard',
          featureGate: 'autonomousOptimizer',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>AI-powered schedule optimization that continuously monitors and suggests improvements to your daily workflow.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      How It Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The Autonomous Dayboard monitors your schedule in real-time and detects optimization opportunities:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Gap Detection:</strong> Identifies idle time between tickets</li>
                      <li><strong>Overload Alerts:</strong> Flags technicians running over capacity</li>
                      <li><strong>Skill Mismatches:</strong> Suggests reassignments for better proficiency fit</li>
                      <li><strong>Promise Risk:</strong> Warns when waiter tickets may miss due time</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Provisional Add-On Mode</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When jobs are added to an existing ticket that increase OptiTime:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>System calculates impact on schedule</li>
                      <li>Offers "Absorb" (keep current slot) or "Reflow" (optimize around it)</li>
                      <li>Works for any phase: diagnostic, mid-repair, or upsell scenarios</li>
                      <li>Respects locked tickets as immovable anchors</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Optimization Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When triggered, the simulator presents options:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Impact Radius:</strong> Number of tickets affected by each option</li>
                      <li><strong>Reassignment Count:</strong> How many tickets change technicians</li>
                      <li><strong>Thrash Score:</strong> Measure of schedule disruption</li>
                      <li><strong>Preview Mode:</strong> See changes before applying</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Willingness Controls</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Configure in Shop Culture settings:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Willingness to Reassign:</strong> Max tickets that can change assignment</li>
                      <li><strong>Locked Ticket Behavior:</strong> Locked tickets are never moved</li>
                      <li><strong>Waiter Priority:</strong> Waiter due times take precedence</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'job-weight-editor',
          title: 'Job Weight Editor',
          featureGate: 'jobWeightEditor',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>Fine-tune how OptiAssign weighs different factors when matching technicians to tickets.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gauge className="h-4 w-4" />
                      Assignment Weights
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Adjust the relative importance of each factor:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Proficiency Weight:</strong> How much skill match matters</li>
                      <li><strong>Workload Weight:</strong> Balance vs. specialization preference</li>
                      <li><strong>Efficiency Weight:</strong> Past performance on similar jobs</li>
                      <li><strong>Bay Match Weight:</strong> Importance of correct bay type</li>
                      <li><strong>Comeback Bonus:</strong> Priority for returning work to original tech</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Category Overrides</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Set different weights per job category:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Diagnostics: Higher proficiency weight (skill critical)</li>
                      <li>Maintenance: Higher efficiency weight (throughput matters)</li>
                      <li>Specialty: Higher bay match weight (equipment required)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing the Editor</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Settings → OptiAssign → Weights</strong> to configure assignment weights. Changes apply immediately to new assignments.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'optiassign-tuning',
          title: 'OptiAssign Tuning',
          featureGate: 'optiAssignTuning',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>Advanced configuration options for the OptiAssign algorithm to match your shop's unique workflow.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Tuning Options
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Proficiency Threshold:</strong> Minimum skill % required for assignment (default: 70%)</li>
                      <li><strong>Idle Gap Penalty:</strong> How much to penalize creating gaps between tickets</li>
                      <li><strong>Specialist Protection:</strong> Reduce assignment of high-skill techs to basic work</li>
                      <li><strong>Tier Gap Penalty:</strong> Discourage assigning L5 work to L2 techs</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Diagnostic Quality Integration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When enabled, DCS (Diagnostic Competency Score) influences assignments:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Up to 5% bonus for high DCS on diagnostic tickets</li>
                      <li>Never penalizes - only provides positive boost</li>
                      <li>Toggle via "Diagnostic Quality Weight" setting</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Historical Affinity</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>OptiTime learning data influences assignments:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Techs with proven success on vehicle+job combos get boost</li>
                      <li>Comeback patterns reduce priority for affected tech</li>
                      <li>Configurable via "Use Historical Affinity" toggle</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing Tuning</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Navigate to <strong>Settings → OptiAssign</strong> to access all tuning options. Most settings take effect on the next assignment operation.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'advisor-intelligence',
          title: 'Advisor Intelligence (Role Intelligence)',
          featureGate: 'roleIntelligence',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST+ TIER</Badge>
              <p>Advanced analytics for service advisors that surface decision patterns, coaching opportunities, and workflow optimization insights.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Decision Event Tracking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>System automatically captures advisor decisions:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Scheduling Overrides:</strong> When advisor ignores OptiPlace suggestion</li>
                      <li><strong>Waiter Conversions:</strong> Converting waiters to drop-offs (and reasons)</li>
                      <li><strong>Late Authorizations:</strong> Customer approvals that delay work start</li>
                      <li><strong>Midday Waiters:</strong> Accepting waiters during peak hours</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Advisor Effectiveness Index (AEI)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Composite score measuring advisor workflow quality:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Auth Success Rate:</strong> Customer authorization conversion</li>
                      <li><strong>Auth Latency:</strong> Time from ticket creation to approval</li>
                      <li><strong>Promise Accuracy:</strong> Completion time prediction accuracy</li>
                      <li><strong>Declined Return Rate:</strong> How often declined work comes back</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Self-View Access</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Advisors can opt-in to view their own metrics:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Requires 30+ sample events for reliable data</li>
                      <li>Self-unlock is optional (privacy-first design)</li>
                      <li>Managers always see all advisor metrics</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Coaching Insights</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>System surfaces coaching opportunities based on patterns: high stall rates, late authorizations, override patterns that don't improve outcomes. Available in the Analytics → Advisors tab.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'diagnostic-ai-assistance',
          title: 'Diagnostic AI Assistance',
          featureGate: 'diagAiEnabled',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">CATALYST TIER</Badge>
              <p>AI-powered diagnostic support that adapts to technician skill level and provides just-in-time guidance during diagnostic workflows.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Adaptive AI Phases
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>AI assistance level adapts to technician proficiency:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Phase 1 (Novice):</strong> Step-by-step guidance, detailed explanations</li>
                      <li><strong>Phase 2 (Developing):</strong> Key checkpoints, reference links</li>
                      <li><strong>Phase 3 (Proficient):</strong> Summary hints, skip-ahead options</li>
                      <li><strong>Phase 4 (Expert):</strong> Minimal prompts, advanced tools only</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Just-In-Time Training</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>During diagnostic flows, relevant training appears:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Training artifacts linked to current diagnostic step</li>
                      <li>Video clips, wiring diagrams, TSBs shown in sidebar</li>
                      <li>Completion tracked in Learning Center history</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">AI Output Types</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Step Hint:</strong> Contextual guidance for current step</li>
                      <li><strong>Summary:</strong> Quick recap of diagnostic progress</li>
                      <li><strong>Next Steps:</strong> Suggested actions based on findings</li>
                      <li><strong>Reference:</strong> Links to relevant documentation</li>
                    </ul>
                    <Alert className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        All AI outputs include a disclaimer that they are suggestions only - final diagnosis is technician's responsibility.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Behavior Signals</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>System tracks diagnostic behavior to improve AI phase calibration: step skipping patterns, time-on-step, hint usage, and confirmation outcomes.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'eod-rollover',
          title: 'End-of-Day Rollover Management',
          content: (
            <div className="space-y-4">
              <p>Handle tickets that won't complete by end of day:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <RotateCcw className="h-4 w-4" />
                      EOD Rollover Container
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>As end-of-day approaches, tickets projected to finish after closing are flagged. The EOD Rollover panel shows these tickets and offers options for handling them.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Rollover Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-2">
                      <li><strong>Auto Rollover:</strong> Automatically move incomplete tickets to the next business day</li>
                      <li><strong>Manual Assignment:</strong> Choose specific technician and time slot for next day</li>
                      <li><strong>Split Ticket:</strong> Complete partial work today, roll remaining to tomorrow</li>
                      <li><strong>Move to Parking Lot:</strong> Hold for manual scheduling</li>
                      <li><strong>Convert to Drop-off:</strong> Change waiter to drop-off for next day pickup</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <PlayCircle className="h-4 w-4" />
                      Resume Anyway Button
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>When resuming a paused ticket that would push finish past technician's shift end:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>The EOD Rollover Modal appears with the "Resume Anyway" button</li>
                      <li>Click "Resume Anyway" to force-start the timer despite EOD conflict</li>
                      <li>Useful for overtime situations or urgent work</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Catalyst Auto-Rollover <Badge variant="outline" className="ml-2 text-xs">CATALYST</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Catalyst tier shops benefit from automatic rollover ticket creation:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>When OptiFinish exceeds technician's shift end + buffer, rollover triggers automatically</li>
                      <li>Part A (today) is locked at clock-out; Part B (tomorrow) is created</li>
                      <li>AutoBump/RunLong applies only to Part B after locking</li>
                      <li>Toast notification confirms rollover creation</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Core tier users see prompts for manual rollover decisions.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Rollover Badges & Linking</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Rolled-over tickets display a 🔄 badge on the Dayboard showing they continued from a previous day.</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Linked via rollover_of_ticket_id for audit trail</li>
                      <li>Part B validates against Due Out during creation</li>
                      <li>EOD boundaries use per-day technician hours in shop timezone</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'weather-outdoor-bays',
          title: 'Weather-Aware Outdoor Bays',
          content: (
            <div className="space-y-4">
              <p>See when the weather conflicts with the outdoor-work conditions your shop has defined, with the reading and your threshold shown together. The shop decides what matters; OptiBay reports whether today crosses that line.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CloudRain className="h-4 w-4" />
                      Feature Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The weather system reports conditions for your shop's location and tells you when they conflict with the outdoor-work thresholds you set. It is advisory: it explains the conflict and shows the reading and your threshold side by side. It does not remove outdoor bays, and it does not move, cancel or reassign work.</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Weather reported for your shop's location via Open-Meteo</li>
                      <li>An advisory on the board when conditions cross your thresholds</li>
                      <li>Outdoor bays remain selectable — you can always proceed</li>
                      <li>Forecasts are labelled as forecasts, never as observed conditions</li>
                      <li>If the weather service cannot be reached, it says so rather than implying conditions are fine</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Shop Location Setup</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Weather features require your shop's location:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li>Navigate to <strong>Shop Profile → General Info</strong></li>
                      <li>Enter your complete address (Street, City, State, Postal Code, Country)</li>
                      <li>System automatically geocodes and stores coordinates</li>
                      <li>Weather data fetches based on your shop's location</li>
                    </ol>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Weather Settings Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Configure weather thresholds in <strong>Shop Profile → General Info</strong>:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Minimum Outdoor Temperature:</strong> Set the lowest temperature (°F) for outdoor work (default: 40°F)</li>
                      <li><strong>Block When Raining:</strong> Prevent outdoor bay scheduling during rain</li>
                      <li><strong>Block When Snowing:</strong> Prevent outdoor bay scheduling during snow</li>
                      <li><strong>Block When Storms:</strong> Prevent outdoor bay scheduling during thunderstorms</li>
                    </ul>
                    <p className="mt-2 text-muted-foreground text-xs">Toggle each condition on/off based on your shop's tolerance and equipment.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Marking Bays as Outdoor</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Designate outdoor bays in the Shop Floor Editor:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li>Navigate to <strong>Birdseye → Configure Layout</strong></li>
                      <li>Select a bay to edit</li>
                      <li>Check the <strong>"☀️ Outdoor / Weather-Sensitive Bay"</strong> checkbox</li>
                      <li>Save layout</li>
                    </ol>
                    <p className="mt-2 text-muted-foreground text-xs">Outdoor bays display a sun badge on Birdseye and Dayboard views.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Weather Blocking Behavior</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>When weather conditions violate your settings:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Outdoor bays are automatically removed from OptiAssign candidate pools</li>
                      <li>Weather-lock overlay appears on affected bays in Birdseye view</li>
                      <li>Tooltip shows reason (e.g., "Currently raining" or "Temp below 40°F")</li>
                      <li>Manual drag-and-drop shows warning modal before allowing override</li>
                    </ul>
                    <p className="mt-2"><strong>Weather Change Alerts:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>System monitors forecast changes every 15 minutes</li>
                      <li>Alert banner appears if scheduled outdoor work becomes affected</li>
                      <li>Options to dismiss, find indoor alternative, or reschedule</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'shop-cameras',
          title: 'Cameras (Optional)',
          content: (
            <div className="space-y-4">
              <p>
                Cameras are optional. OptiBay works normally without them — Guided Start, Final Stop, parking,
                Birdseye and pickup never need a camera.
              </p>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Registering cameras and what they can see</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                    <li>Navigate to <strong>Birdseye → Configure Layout</strong> and open the <strong>Cameras</strong> tab (owners and managers)</li>
                    <li><strong>Add camera:</strong> a name, Interior or Exterior, and what it can do — vehicle / space occupancy, entry / exit transitions, license plate reading</li>
                    <li><strong>Coverage:</strong> tap the bays and parking spots it can see (select all or a range for long rows), add coarse lot areas such as "South Lot", and lanes or gates as Entrance and/or Exit</li>
                    <li><strong>Disable</strong> keeps a camera's setup but marks it to be ignored; <strong>Archive</strong> retires it while keeping its history</li>
                  </ol>
                  <p className="mt-2 text-muted-foreground text-xs">
                    Coverage records what a camera is able to observe so future vehicle observations can be interpreted
                    correctly. It is not occupancy: a place no camera covers is simply unobserved, never assumed empty.
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Cameras are not connected to OptiBay yet. Nothing here streams video, reads plates or moves vehicles —
                    each camera shows "No connection data yet" until a future connection reports in.
                  </p>
                </CardContent>
              </Card>
            </div>
          )
        },
        {
          id: 'specialty-bays',
          title: 'Specialty Bays System',
          content: (
            <div className="space-y-4">
              <p>Configure bays for specific types of work and equipment:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gauge className="h-4 w-4" />
                      Bay Types Available
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Regular Lifts:</strong> 2-post, 4-post, and scissor lifts for general work</li>
                      <li><strong>Flat Bays:</strong> Ground-level service areas</li>
                      <li><strong>Alignment Bays:</strong> Equipped with four-wheel alignment racks</li>
                      <li><strong>ADAS Bays:</strong> Advanced driver assistance system calibration equipment</li>
                      <li><strong>Wash Bays:</strong> Vehicle cleaning and detailing areas</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Set up bay types and assignments in the <strong>Shop Floor Editor</strong>. Assign technicians to specific bay types based on their certifications and equipment access.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Specialty Tab View</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>The Specialty Bays tab on the Dashboard shows a dedicated view of tickets requiring specialty equipment, making it easy to manage alignment, ADAS, and wash jobs separately from general repair work.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Automatic Job Routing</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside space-y-1">
                      <li>System automatically identifies jobs requiring specialty bays</li>
                      <li>Tickets with specialty jobs only assigned to techs with bay access</li>
                      <li>Visual badges on tickets show specialty requirements</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'board-optimizer',
          title: 'Board Optimizer',
          content: (
            <div className="space-y-4">
              <p>Automatically optimize your entire schedule:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Smart Reordering:</strong> Rearranges tickets for maximum efficiency</li>
                <li><strong>Skill Optimization:</strong> Matches technicians to jobs based on proficiency</li>
                <li><strong>Capacity Balancing:</strong> Distributes workload evenly across technicians based on shop settings</li>
                <li><strong>Preview Changes:</strong> Review suggested changes before applying</li>
              </ul>
              <div className="bg-muted p-4 rounded-lg mt-4">
                <p className="text-sm"><strong>When to Use:</strong> Run the optimizer at the start of each day or when the schedule becomes inefficient.</p>
              </div>
            </div>
          )
        },
        {
          id: 'vehicle-catalog',
          title: 'Vehicle Catalog Management',
          content: (
            <div className="space-y-4">
              <p>System automatically imports comprehensive vehicle catalog based on selected packages:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Package Selection:</strong> Choose vehicle types your shop services (Asian, Domestic, Diesel, Euro, EV, Exotic)</li>
                <li><strong>Automatic Import:</strong> System imports relevant vehicle data based on your package choices</li>
                <li><strong>Data Filtering:</strong> Only selected vehicle types appear in VIN decoder and dropdowns</li>
                <li><strong>Seamless Updates:</strong> Vehicle catalog updates are handled automatically for all users</li>
              </ul>
            </div>
          )
        },
        {
          id: 'staff-schedules',
          title: 'Staff Schedules',
          content: (
            <div className="space-y-4">
              <p>Configure work schedules for managers and service writers. This is used for time clock reporting only and does not affect scheduling.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Purpose
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Staff schedules define when non-technician team members (managers and service writers) are expected to work. This information is used exclusively for:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Time clock reporting</li>
                      <li>Attendance tracking</li>
                      <li>Payroll reference</li>
                    </ul>
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Note</AlertTitle>
                      <AlertDescription>
                        Staff schedules do not affect ticket scheduling or assignment. Only technician schedules impact the optimizer and OptiAssign.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="mb-2">Access staff schedules in Shop Profile → Staff Schedules tab:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li><strong>Automatic Creation:</strong> Schedules auto-created for all managers/service writers (8:00 AM - 5:00 PM, M-F default)</li>
                      <li><strong>Edit Schedule:</strong> Customize shift times, working days, and breaks</li>
                      <li><strong>Set Working Days:</strong> Select which days staff member works</li>
                      <li><strong>Configure Breaks:</strong> Recurring (daily) and non-recurring (one-time) breaks</li>
                      <li><strong>Time Off:</strong> Mark specific dates when staff member is absent</li>
                    </ol>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Permissions</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Only managers can view and edit staff schedules. Service writers and technicians cannot access this feature.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'billing-subscription',
          title: 'Billing & Subscription',
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN'],
          productArea: 'Billing',
          content: (
            <div className="space-y-4">
              <p>Manage your OptiBay Core subscription, view invoices, and update payment methods.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Current Plan Card
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Displays your active subscription information:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Plan Name:</strong> Your current subscription tier</li>
                      <li><strong>Status:</strong> Active, Trialing, or Past Due</li>
                      <li><strong>Subscription End:</strong> When current billing period ends</li>
                      <li><strong>Manage Subscription:</strong> Opens Stripe Customer Portal</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Changing Plans</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Scroll to "Change Plan" section</li>
                      <li>Review plan features and pricing</li>
                      <li>Click "Choose Plan" on desired tier</li>
                      <li>Complete checkout in Stripe's portal</li>
                      <li>Plan updates immediately upon payment</li>
                    </ol>
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Plan Changes</AlertTitle>
                      <AlertDescription>
                        Upgrades take effect immediately. Downgrades take effect at end of billing period. You'll receive prorated credits for early upgrades.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Invoices List</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>View your billing history (up to 12 most recent):</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Date:</strong> When invoice was created</li>
                      <li><strong>Amount:</strong> Total charged (USD)</li>
                      <li><strong>Status:</strong> Paid, Pending, or Overdue</li>
                      <li><strong>Billing Period:</strong> Date range covered</li>
                      <li><strong>Download:</strong> Click "Open Invoice" for PDF</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Payment Method Management</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="mb-2">To update your payment method:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Click "Manage Subscription" button</li>
                      <li>Navigate to "Payment Methods" in portal</li>
                      <li>Add or update payment method</li>
                      <li>Set as default for future charges</li>
                    </ol>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'team-management',
          title: 'Team Management',
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN'],
          productArea: 'Team & Permissions',
          content: (
            <div className="space-y-4">
              <p>Invite team members, assign roles, and manage access permissions for your shop.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Inviting Team Members
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Click <strong>"Invite Member"</strong> button</li>
                      <li>Enter team member's email address</li>
                      <li>Select their role (Manager, Service Writer, or Technician)</li>
                      <li>Click <strong>"Send Invitation"</strong></li>
                      <li>Invitee receives email with acceptance link</li>
                      <li>They create account and are added to shop</li>
                    </ol>
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Invitation Process</AlertTitle>
                      <AlertDescription>
                        Invitations are sent via email. Recipient must click link to accept. Links valid for 7 days.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Role Permissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-semibold mb-1">Manager (Full Access)</p>
                      <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground">
                        <li>Full shop management access</li>
                        <li>Manage team members and roles</li>
                        <li>Edit shop profile and all settings</li>
                        <li>Create, edit, and delete tickets</li>
                        <li>Run optimizer and view analytics</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Service Writer (Operations)</p>
                      <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground">
                        <li>Create and manage tickets</li>
                        <li>Assign jobs to technicians</li>
                        <li>View shop profile (read-only)</li>
                        <li>Run optimizer and view analytics</li>
                        <li>Import ticket data (CSV, RO)</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Technician (Limited)</p>
                      <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground">
                        <li>View assigned tickets only</li>
                        <li>Control ticket timers</li>
                        <li>Update ticket status</li>
                        <li>View own schedule</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Managing Members</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Change Roles:</strong> Click role dropdown in member's row</p>
                    <p><strong>Edit Details:</strong> Click "Edit" to update display name</p>
                    <p><strong>Remove Member:</strong> Click "Remove" → confirm deletion</p>
                    <Alert className="mt-2" variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Warning</AlertTitle>
                      <AlertDescription className="text-xs">
                        Removing a technician doesn't delete their profile or work history. It only removes account access.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Access Requirements</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Only managers can access Team Management page. Other roles see "Access Denied" message.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'account-settings',
          title: 'Account Settings',
          content: (
            <div className="space-y-4">
              <p>Manage your personal account settings, update your profile, and change your password.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCircle className="h-4 w-4" />
                      Accessing Account Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Click your profile icon in the top navigation bar, then select <strong>Account Settings</strong> from the dropdown menu.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Profile Tab</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Display Name:</strong> Update how your name appears throughout the system</li>
                      <li><strong>Email:</strong> View your login email (contact support to change)</li>
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">Changes to display name are reflected immediately across all areas of the application.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      Security Tab
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Update your password:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li>Enter your new password (minimum 8 characters)</li>
                      <li>Confirm the new password</li>
                      <li>Click <strong>Update Password</strong></li>
                    </ol>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Use a strong, unique password. Combine uppercase, lowercase, numbers, and symbols for best security.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Appearance Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Toggle between light and dark modes using the theme switch. Your preference is saved locally and persists across sessions.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'data-import',
          title: 'Data Import & Setup',
          content: (
            <div className="space-y-4">
              <p>Import and configure shop data including vehicles, jobs catalog, and initial setup.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Vehicle Catalog Import
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The vehicle catalog is automatically configured based on your selected vehicle packages:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Asian:</strong> Japanese and Korean vehicles</li>
                      <li><strong>Domestic:</strong> American manufacturers</li>
                      <li><strong>Euro:</strong> European vehicles</li>
                      <li><strong>Diesel:</strong> Diesel engine vehicles</li>
                      <li><strong>EV:</strong> Electric and hybrid vehicles</li>
                      <li><strong>Exotic:</strong> Luxury and specialty vehicles</li>
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">Select packages in Shop Profile → General Info. VIN decoder and vehicle dropdowns filter based on selected packages.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Jobs Catalog</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The jobs catalog comes pre-loaded with comprehensive automotive service entries. Additional customization:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Custom Jobs:</strong> Add custom jobs during ticket creation</li>
                      <li><strong>Time Overrides:</strong> Adjust default times per job in inline editor</li>
                      <li><strong>Bay Requirements:</strong> System assigns bay types automatically based on job category</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">GeoVehicle Data</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Import vehicle-specific data for enhanced job routing:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>ADAS capability detection by year/make/model</li>
                      <li>Engine specifications for job matching</li>
                      <li>Transmission types and drivetrain info</li>
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">GeoVehicle imports are managed by system administrators.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Initial Shop Setup</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Complete these steps for optimal system configuration:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li>Configure shop hours and holidays</li>
                      <li>Set up bays in Shop Floor Editor</li>
                      <li>Add technicians with schedules and skills</li>
                      <li>Configure Shop Culture settings</li>
                      <li>Set up weather integration (if outdoor bays)</li>
                    </ol>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'invoice-upload-csv',
          title: 'Invoice Upload & CSV Import',
          // TDO-1: financial import help — gate to billing/advisor roles.
          requiredRoles: ['OWNER', 'MANAGER', 'ORG_ADMIN', 'SERVICE_WRITER'],
          content: (
            <div className="space-y-4">
              <p>Quickly create tickets by uploading repair orders (RO) or importing CSV files.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Repair Order (RO) Upload
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Upload images/PDFs of repair orders to auto-extract data using OCR and AI:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li>Click or drag RO into upload zone (max 10MB, 5 pages)</li>
                      <li>System extracts text using OCR</li>
                      <li>AI parses: ticket#, vehicle info, customer data, jobs</li>
                      <li>Extracted data populates form</li>
                      <li>Review and adjust as needed</li>
                      <li>Submit ticket</li>
                    </ol>
                    <Alert className="mt-2">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Supported Formats</AlertTitle>
                      <AlertDescription className="text-xs space-y-1">
                        <p><strong>Images:</strong> JPG, PNG, WEBP</p>
                        <p><strong>PDFs:</strong> Up to 5 pages (text or scanned)</p>
                        <p><strong>Max Size:</strong> 10MB</p>
                      </AlertDescription>
                    </Alert>
                    <div className="mt-2">
                      <p className="font-semibold text-xs mb-1">Tips for Best Results:</p>
                      <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground">
                        <li>Use clear, high-resolution images</li>
                        <li>Ensure good lighting, minimal glare</li>
                        <li>Align document straight</li>
                        <li>Always review extracted data</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      CSV Import (Bulk Upload)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Import multiple tickets at once from CSV file:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1 text-xs">
                      <li>Prepare CSV file per template</li>
                      <li>Click "Import Tickets from CSV"</li>
                      <li>Select target date using calendar modal</li>
                      <li>System validates data</li>
                      <li>Review import report (successes/errors)</li>
                      <li>Re-importing same file won't create duplicates</li>
                    </ol>
                    <div className="mt-2">
                      <p className="font-semibold text-xs mb-1">CSV Requirements:</p>
                      <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground">
                        <li><strong>Required:</strong> ticket_number, customer_name, year, make, model</li>
                        <li><strong>Jobs:</strong> job_1, hours_1, job_2, hours_2, etc.</li>
                        <li><strong>Optional:</strong> phone, vin, license, engine, ticket_type</li>
                        <li><strong>Format:</strong> UTF-8, comma-delimited</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">When to Use Each</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p className="font-semibold text-xs">Use RO Upload:</p>
                    <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground mb-2">
                      <li>Single tickets as customers arrive</li>
                      <li>Have physical/scanned RO documents</li>
                      <li>Extract job details from narratives</li>
                    </ul>
                    <p className="font-semibold text-xs">Use CSV Import:</p>
                    <ul className="list-disc list-inside text-xs ml-2 space-y-1 text-muted-foreground">
                      <li>Bulk import daily appointments</li>
                      <li>Migrate from another system</li>
                      <li>Import 10+ tickets at once</li>
                      <li>Need idempotent imports</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'calendar-navigation',
          title: 'Calendar Navigation',
          content: (
            <div className="space-y-4">
              <p>Navigate between days and visualize shop utilization using the inline calendar and calendar modal.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Inline Calendar (Top Bar)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Date Display:</strong> Shows current viewing date</li>
                      <li><strong>Previous/Next:</strong> Arrow buttons to navigate days</li>
                      <li><strong>Today Button:</strong> Jump back to current date</li>
                      <li><strong>Keyboard Shortcuts:</strong> Left/Right arrows, T for today</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Maximize2 className="h-4 w-4" />
                      Calendar Modal (Month View)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p className="text-xs">Click date display in top bar to open full calendar:</p>
                    <div className="space-y-2 mt-2">
                      <div>
                        <p className="font-semibold text-xs mb-1">Shop Hours Integration</p>
                        <p className="text-xs text-muted-foreground">Closed days are grayed out. Only shop hours are selectable.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs mb-1">Holiday Recognition</p>
                        <p className="text-xs text-muted-foreground">Holidays from Shop Profile marked as closed.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs mb-1">Utilization Indicators</p>
                        <p className="text-xs text-muted-foreground mb-1">Colored dots show capacity:</p>
                        <div className="space-y-1 ml-2">
                          <div className="flex items-center gap-2 text-xs">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span className="text-muted-foreground">Green: 0-30% (Light)</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <div className="h-2 w-2 rounded-full bg-yellow-500" />
                            <span className="text-muted-foreground">Yellow: 30-60% (Moderate)</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <div className="h-2 w-2 rounded-full bg-orange-500" />
                            <span className="text-muted-foreground">Orange: 60-90% (High)</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <div className="h-2 w-2 rounded-full bg-red-500" />
                            <span className="text-muted-foreground">Red: 90-100% (Near capacity)</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <div className="h-2 w-2 rounded-full bg-black" />
                            <span className="text-muted-foreground">Black: 100%+ (Over capacity)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">CSV Import Date Selection</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="text-xs">When importing CSV tickets, calendar modal opens to choose target day. Ensures tickets land on correct working day.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Business Day Mode</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="text-xs">When enabled in Shop Profile, calendar skips closed days when navigating. Weekends and holidays are bypassed.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'obli-load-index',
          title: 'OBLI (OptiBay Load Index)',
          content: (
            <div className="space-y-4">
              <p>The <strong>OptiBay Load Index (OBLI)</strong> is a single, easy-to-read score that tells you how heavy or light a day is expected to be. Instead of guessing whether tomorrow will be slammed or smooth, OBLI gives you a clear, color-coded indicator right on your calendar.</p>
              
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gauge className="h-4 w-4" />
                      How OBLI Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <p>OBLI looks at five major factors that affect how busy and stressful a day can become:</p>
                    <div className="space-y-2 ml-2">
                      <div>
                        <p className="font-semibold text-xs">1. Tech Availability</p>
                        <p className="text-xs text-muted-foreground">How many technicians are available to work that day and how much time they have to take on jobs.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs">2. Capacity Strain</p>
                        <p className="text-xs text-muted-foreground">How close the day is to reaching the shop's maximum working hours. Lower strain = lower OBLI, high strain = higher OBLI.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs">3. Waiter Load</p>
                        <p className="text-xs text-muted-foreground">Waiters create time pressure and limit flexibility, so more waiters increases the OBLI.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs">4. Specialty Job Mix</p>
                        <p className="text-xs text-muted-foreground">ADAS work, alignments, or other specialty tasks that tie up bays add extra complexity.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs">5. Bay Utilization</p>
                        <p className="text-xs text-muted-foreground">Measures how heavily your bays are booked.</p>
                      </div>
                    </div>
                    <p className="text-xs mt-2">Each factor contributes to the final score from <strong>0 to 100</strong>.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">What the Numbers Mean</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p className="text-xs mb-2">OBLI is designed so that:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>LOW SCORES</strong> = smooth workload</li>
                      <li><strong>HIGH SCORES</strong> = heavier workload</li>
                    </ul>
                    
                    <div className="mt-3">
                      <p className="font-semibold text-xs mb-2">Color Guide:</p>
                      <div className="space-y-1 ml-2">
                        <div className="flex items-center gap-2 text-xs">
                          <div className="h-2 w-2 rounded-full bg-green-500" />
                          <span><strong>Green (0-39):</strong> Light day</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <div className="h-2 w-2 rounded-full bg-yellow-500" />
                          <span><strong>Yellow (40-69):</strong> Moderate day</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <div className="h-2 w-2 rounded-full bg-orange-500" />
                          <span><strong>Orange (70-89):</strong> High load day</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <div className="h-2 w-2 rounded-full bg-red-500" />
                          <span><strong>Red (90-100):</strong> Heavy day</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Where You'll See OBLI
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <div className="space-y-2">
                      <div>
                        <p className="font-semibold text-xs">1. Calendar View</p>
                        <p className="text-xs text-muted-foreground">Every date shows a small colored dot for quick scanning.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs">2. Day Summary Hover Card</p>
                        <p className="text-xs text-muted-foreground">Hovering over a date shows total tickets, booked hours, capacity percentage, specialty job counts, waiters vs drop-offs, and <strong>OBLI with score and explanation</strong>.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-xs">3. Future Forecasting</p>
                        <p className="text-xs text-muted-foreground">Use OBLI to see which days can handle more appointments, should avoid scheduling waiters, require more technician coverage, or are ideal for big vs quick jobs.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Why OBLI Matters
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="text-xs mb-2">OBLI helps shops:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li>Reduce unexpected stress days</li>
                      <li>Prevent overbooked technicians</li>
                      <li>Balance workloads across the week</li>
                      <li>Improve customer promise accuracy</li>
                      <li>Protect technician morale</li>
                      <li>Avoid stacking specialty jobs in bottleneck bays</li>
                      <li>Plan staffing intelligently</li>
                    </ul>
                    <p className="text-xs mt-2 italic">It's your shop's <strong>daily weather forecast</strong> — but for workload instead of temperature.</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      How OBLI Helps in Real Life
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                      <li><strong>Adding jobs:</strong> Quickly see which days can take more load</li>
                      <li><strong>Scheduling waiters:</strong> Avoid placing waiters on high-OBLI days</li>
                      <li><strong>Work distribution:</strong> Identify days where techs need help or extra coverage</li>
                      <li><strong>Capacity planning:</strong> Understand your true shop limits day-to-day</li>
                      <li><strong>Strategic scheduling:</strong> Reduce chaos and increase consistency</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'inspection-checklist',
          title: 'OptiDVI — Inspection Checklist',
          content: (
            <div className="space-y-4">
              <p>The in-ticket Digital Vehicle Inspection workflow allows technicians to perform structured inspections during service.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ScanLine className="h-4 w-4" />
                      Inspection Workflow
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Each inspection is organized by systems and items:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Systems:</strong> Groups of related inspection points (Brakes, Suspension, Fluids, etc.)</li>
                      <li><strong>Items:</strong> Individual inspection points within each system</li>
                      <li><strong>Conditions:</strong> Rate each item as Green (OK), Yellow (May Need Attention), or Red (Needs Immediate Attention)</li>
                      <li><strong>Photos:</strong> Attach photo evidence to any inspection item</li>
                      <li><strong>Notes:</strong> Add text notes describing findings</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Starting an Inspection</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Start inspections from the Ticket Workflow Panel → Inspections section. Select a published inspection template, then work through each system and item. Save progress at any time and complete when finished.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'inspection-templates',
          title: 'OptiDVI — Inspection Templates Editor',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">MANAGER ONLY</Badge>
              <p>Create and manage inspection templates that define the structure of your shop's vehicle inspections.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      3-Panel Structural Editor
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>The editor uses a three-panel layout:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Panel 1 — Systems:</strong> Add, rename, reorder inspection system groups</li>
                      <li><strong>Panel 2 — Items:</strong> Add, edit, reorder items within the selected system</li>
                      <li><strong>Panel 3 — Item Editor:</strong> Configure item properties including name, description, evidence requirements, and AI policy</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Clone-on-Edit Versioning</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Published templates are immutable. When you edit a published template:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>A new draft version is created automatically</li>
                      <li>Edit the draft freely without affecting active inspections</li>
                      <li>Publish when ready — the new version becomes active</li>
                      <li>Version history with change notes is maintained</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Drag-and-Drop Reordering</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Reorder systems and items within the editor using drag-and-drop. Changes are saved to the draft automatically.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'dvi-customer-view',
          title: 'OptiDVI — Customer View',
          content: (
            <div className="space-y-4">
              <p>A public, token-based page (<strong>/dvi/view/:token</strong>) where customers review inspection findings shared by the shop.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Customer Experience
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Findings are grouped by condition for clear presentation:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Red — Needs Immediate Attention:</strong> Safety-critical items requiring urgent action</li>
                      <li><strong>Yellow — May Need Attention:</strong> Items to monitor or address soon</li>
                      <li><strong>Green — OK:</strong> Items that passed inspection</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Finding Details</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Each finding card includes:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Cover thumbnail photo from the inspection</li>
                      <li>Labor estimate for recommended work</li>
                      <li>"What We'll Do" procedure outline</li>
                      <li><strong>Approve/Decline</strong> buttons for customer authorization</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sharing</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Share the DVI link with customers via SMS or email. The token-based URL requires no login — customers simply open the link to view their inspection results.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'dvi-intelligence',
          title: 'OptiDVI — Intelligence Dashboard',
          featureGate: 'dviNetworkIntelligence' as FeatureKey,
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">ENTERPRISE</Badge>
              <p>Advanced analytics for your inspection program, providing quality scores, trends, and network benchmarking.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Dashboard Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Quality Scores:</strong> Inspection completeness, photo coverage, note quality</li>
                      <li><strong>Approval Trends:</strong> Customer approval rates over time by condition</li>
                      <li><strong>Drift Alerts:</strong> Notifications when inspection quality drops below thresholds</li>
                      <li><strong>Benchmark Comparison:</strong> Compare your shop's DVI metrics against network averages</li>
                      <li><strong>Network Opt-In:</strong> Participate in anonymized network benchmarking</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'bay-rules-review',
          title: 'Bay Rules Review',
          content: (
            <div className="space-y-4">
              <Badge variant="outline" className="mb-2">MANAGER ONLY</Badge>
              <p>Review and approve AI-generated bay classifications for jobs in your catalog.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" />
                      Review Tabs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Needs Review:</strong> New AI classifications awaiting manager review</li>
                      <li><strong>Pending:</strong> Classifications under review</li>
                      <li><strong>Approved:</strong> Accepted bay type assignments</li>
                      <li><strong>Modified:</strong> Classifications where manager overrode the AI suggestion</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Editing Classifications</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>For each job, you can:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Edit the assigned bay type (Regular, Alignment, ADAS, Wash)</li>
                      <li>Set bay capabilities required</li>
                      <li>Add bay type exclusions</li>
                      <li>Trigger batch AI classification for unclassified jobs</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Impact</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Approved bay classifications feed into the bay gating system, ensuring tickets are only assigned to technicians with access to the correct bay type.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'chronicle',
      title: 'OptiChronicle Customer Portal',
      icon: Smartphone,
      subsections: [
        {
          id: 'chronicle-overview',
          title: 'Portal Overview',
          content: (
            <div className="space-y-4">
              <p>OptiChronicle is a separate, mobile-first customer portal at <strong>/chronicle/*</strong> with its own authentication, layout, and navigation.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Smartphone className="h-4 w-4" />
                      Purpose
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Chronicle gives your customers self-service access to:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>View their vehicles and service history</li>
                      <li>Review and approve/decline service recommendations</li>
                      <li>View DVI inspection findings</li>
                      <li>Message the shop directly</li>
                      <li>Receive notifications about service updates</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Authentication & Shop Linking</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Customers create their own account and link to your shop via an invitation or phone number match. The portal uses separate authentication from the staff-facing application.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Availability</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Chronicle is enabled via the <code>VITE_ENABLE_CHRONICLE</code> environment variable. When enabled, all Chronicle routes become available to customers.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-home',
          title: 'Home Dashboard',
          content: (
            <div className="space-y-4">
              <p>The Chronicle home screen provides an at-a-glance overview of the customer's shop relationship.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Dashboard Widgets</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Active Tickets:</strong> Currently in-progress service tickets</li>
                      <li><strong>Upcoming Recommendations:</strong> Pending service recommendations from the shop</li>
                      <li><strong>Recent Activity:</strong> Latest updates on vehicles and services</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-garage',
          title: 'My Garage',
          content: (
            <div className="space-y-4">
              <p>View all vehicles on file at the shop.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Car className="h-4 w-4" />
                      Vehicle List
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Year, make, model for each vehicle</li>
                      <li>Partial VIN and license plate display</li>
                      <li>Click through to vehicle detail with service history</li>
                    </ul>
                    <p className="text-muted-foreground text-xs mt-2">Vehicles appear after your first service visit.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-history',
          title: 'Service History',
          content: (
            <div className="space-y-4">
              <p>Complete history of past service tickets.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ticket List</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Chronological list of completed service visits</li>
                      <li>Vehicle info, date, and ticket status</li>
                      <li>Click through for detailed job breakdown</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ticket Detail</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Each ticket detail shows individual jobs performed, service notes, and completion information.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-recommendations',
          title: 'Recommendations',
          content: (
            <div className="space-y-4">
              <p>View and respond to service recommendations from the shop.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Recommendation Cards</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>Each recommendation shows:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Service title and description</li>
                      <li>Severity level (Info, Soon, Urgent)</li>
                      <li>Source (DVI finding, advisor recommendation, etc.)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Approve / Decline</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Approve recommendations to authorize work, or decline to skip. The shop is notified of your decision in real-time.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-findings',
          title: 'DVI Findings',
          content: (
            <div className="space-y-4">
              <p>View digital vehicle inspection findings shared by the shop, including photos and condition assessments.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Findings View</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Grouped by condition: Red (urgent), Yellow (monitor), Green (OK)</li>
                      <li>Photo evidence from the technician</li>
                      <li>Technician notes and recommendations</li>
                      <li>Links to approve recommended services</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-messages',
          title: 'Messages',
          content: (
            <div className="space-y-4">
              <p>Direct messaging between customers and the shop.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Messaging Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Inbox:</strong> View all message threads with the shop</li>
                      <li><strong>Compose:</strong> Start a new conversation</li>
                      <li><strong>Thread View:</strong> Full conversation history with timestamps</li>
                      <li><strong>Read Receipts:</strong> See when messages have been read</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-notifications',
          title: 'Notifications',
          content: (
            <div className="space-y-4">
              <p>Stay informed about service updates, recommendations, and messages.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Notification System
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Bell icon with unread count in the navigation</li>
                      <li>Notifications for ticket status changes, new messages, and recommendations</li>
                      <li>Severity levels for prioritization</li>
                      <li>Click notifications to navigate to related content</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'chronicle-account',
          title: 'Account',
          content: (
            <div className="space-y-4">
              <p>Manage your Chronicle customer profile.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Profile Management</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Update your display name, contact information, and communication preferences from the Account page.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: HelpCircle,
      subsections: [
        {
          id: 'common-issues',
          title: 'Common Issues',
          content: (
            <div className="space-y-4">
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      VIN Not Decoding
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Solution:</strong> Ensure VIN is exactly 17 characters and consists only of letters and numbers. Some older vehicles may not be in the database - use manual entry instead.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      Reminders Not Sending
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Check:</strong></p>
                    <ul className="list-disc list-inside ml-2">
                      <li>Reminder settings are enabled in Shop Profile</li>
                      <li>Twilio credentials are correct (for SMS)</li>
                      <li>Customer has valid phone/email on ticket</li>
                      <li>Appointment date is in the future</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      OptiFinish Time Incorrect
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Verify:</strong> Technician break schedules are configured correctly and shop hours match actual operating times.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      Can't Assign Ticket to Bay
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Check:</strong></p>
                    <ul className="list-disc list-inside ml-2">
                      <li>Bay has available capacity</li>
                      <li>Technician assigned to bay has required skill level</li>
                      <li>Bay type matches job specialty requirements</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'error-messages',
          title: 'Error Messages Explained',
          content: (
            <div className="space-y-4">
              <p>Common error messages and what they mean:</p>
              <div className="space-y-2 mt-4">
                <div className="p-3 border rounded-lg">
                  <p className="font-mono text-sm text-destructive mb-1">"Insufficient capacity"</p>
                  <p className="text-sm">The bay or technician is at full capacity. Remove or reassign other tickets first.</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="font-mono text-sm text-destructive mb-1">"Invalid time range"</p>
                  <p className="text-sm">The start time is after end time, or times are outside shop hours. Check your time entries.</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="font-mono text-sm text-destructive mb-1">"Duplicate ticket"</p>
                  <p className="text-sm">A ticket with the same VIN already exists for this date. Check existing tickets.</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="font-mono text-sm text-destructive mb-1">"Permission denied"</p>
                  <p className="text-sm">Your role doesn't have access to this feature. Contact your manager if you need access.</p>
                </div>
              </div>
            </div>
          )
        },
        {
          id: 'report-issue',
          title: 'Report Issue',
          content: (
            <div className="space-y-4">
              <p>Found a bug or experiencing an issue? Use the built-in bug reporting system:</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bug className="h-4 w-4" />
                      Report Issue Button
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>A draggable "Report an Issue" button is available throughout the application:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Location:</strong> Floating button (typically bottom-left or bottom-right)</li>
                      <li><strong>Draggable:</strong> Click and drag to reposition for your workflow</li>
                      <li><strong>Position Saved:</strong> Your preferred position persists across sessions</li>
                      <li><strong>Always Available:</strong> Accessible from any page in the application</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Submitting a Bug Report</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Click the "Report an Issue" button</li>
                      <li>Fill out the bug report form with:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>Brief summary of the issue</li>
                          <li>What you were trying to do</li>
                          <li>What actually happened</li>
                          <li>How often it occurs (Always, Sometimes, Once)</li>
                          <li>Severity level (Low, Medium, High, Critical)</li>
                        </ul>
                      </li>
                      <li>Optionally attach screenshots showing the problem</li>
                      <li>Submit the report</li>
                    </ol>
                    <p className="mt-2">The report is automatically sent to support with your shop context, browser info, and current route for faster debugging.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'faq',
          title: 'Frequently Asked Questions',
          content: (
            <div className="space-y-4">
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Can I edit a ticket after it's created?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Yes, managers and service writers can edit all ticket details including customer info, jobs, and assignments. Click the kebab menu (⋮) on any ticket card.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">What happens when a vehicle is marked as a comeback?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>System prioritizes assigning the ticket to the original technician who worked on the vehicle. Ticket shows a 🚨 badge. The "Comeback Nudge" setting in Shop Culture controls how strongly the system prefers the original tech.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How do I unlock a ticket timer?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Timers lock when vehicle is not present or when it's a rollover with incomplete predecessor work. To unlock: mark vehicle as present, or complete/pause the predecessor ticket.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">What does the present vehicle priority setting do?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Gives a time boost to vehicles physically at the shop vs. future appointments. Higher values mean drop-offs get scheduled sooner than appointments, helping reduce vehicle dwell time.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How does the system learn technician efficiency?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Tracks actual completion times vs. predicted times for each job type and technician. Over time, automatically adjusts efficiency and proficiency sliders for more accurate predictions.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Can I override the system's learning?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Yes, manual adjustments to efficiency and proficiency sliders always take precedence over automatic learning. Use this for certifications, training, or other factors the system doesn't detect.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">What's the difference between Dayboard and Birdseye view?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Dayboard is a technician-focused column view for managing work assignments. Birdseye is a visual representation of your physical shop layout showing which bays are occupied and ticket status.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Why can't I start a timer on this ticket?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Check if: 1) Vehicle is marked as present, 2) For rollover tickets, the predecessor work is complete, 3) You have permission for this ticket, 4) The ticket is assigned to you (for technician role).</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How do I add a custom job not in the catalog?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>When adding jobs to a ticket, select "Custom Job" and enter the job details manually. Custom jobs can be saved to the catalog for future use.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Can technicians see all tickets or just their own?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Technicians can see all tickets on the dayboard but can only interact with (start timers, complete jobs) tickets assigned to them.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">What happens to unfinished tickets at end of day?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Non completed tickets automatically rollover to the next days Parking Lot or technicians schedule. They maintain all their details and history. You'll see a rollover badge (🔄) on these tickets.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How do I export data for reporting?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Use the Analytics page to view and export reports. Click the export button in the top right of any analytics chart or table.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">How do completed tickets help with future assignments?</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Every completed ticket builds technician history. The system learns which techs excel at which job types, their actual completion speeds, and builds affinity scores. This makes future OptiPlace assignments more accurate.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        },
        {
          id: 'account-settings',
          title: 'Account Settings',
          content: (
            <div className="space-y-4">
              <p>Manage your personal account settings and preferences.</p>
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCog className="h-4 w-4" />
                      Available Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Display Name:</strong> Edit your name as shown throughout the application</li>
                      <li><strong>Password:</strong> Change your account password</li>
                      <li><strong>Theme:</strong> Toggle between dark and light mode</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Accessing Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Click your profile icon in the top navigation bar and select <strong>Account Settings</strong> from the dropdown menu.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        }
      ]
    }
  ];

  const filteredSections = searchQuery
    ? sections.filter(section =>
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.subsections.some(sub =>
          sub.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : sections;

  return (
    // data-testid: the CORE-BL org-orientation coachmark anchor (Core-100 R4).
    <div className="min-h-screen bg-background" data-testid="user-guide-page">
      <TopBar />
      
      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block w-64 border-r bg-card sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">User Guide</h2>
            <nav className="space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
                      activeSection === section.id
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {section.title}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-12 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">User Guide</h1>
            <p className="text-muted-foreground">
              Comprehensive documentation for the Shop Management System
            </p>
            <Badge variant="outline" className="mt-2">Version 3.0</Badge>
          </div>

          {/* Manual Batch 4: brief role-orientation so each reader knows
              which areas apply to them. Some sections are hidden by role. */}
          <Alert className="mb-8">
            <Info className="h-4 w-4" />
            <AlertTitle>Who should read what</AlertTitle>
            <AlertDescription className="text-sm">
              <strong>Technicians:</strong> job cards, timers, pause/stop handoffs, and your Passport.{' '}
              <strong>Advisors / service writers:</strong> the front office (FOH) queue, ticket handoffs, and customer/advisor workflows.{' '}
              <strong>Managers / owners / org admins:</strong> analytics (including Advisor Performance), team setup, OptiAssets, permissions, and billing.{' '}
              Sections you can't act on may be hidden for your role.
            </AlertDescription>
          </Alert>

          {/* Search */}
          <div className="mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search documentation..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.id} id={section.id} className="scroll-mt-20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h2 className="text-3xl font-bold">{section.title}</h2>
                  </div>

                  <div className="space-y-8">
                    {section.subsections
                      // Manual Batch 3: single predicate — preserves featureGate
                      // behavior and ANDs optional requiredRoles.
                      .filter((subsection) =>
                        canViewGuideItem(
                          subsection,
                          role,
                          featureFlags as Record<string, boolean> | null,
                        ),
                      )
                      .map((subsection) => (
                      <div key={subsection.id} id={subsection.id} className="scroll-mt-20">
                        <h3 className="text-xl font-semibold mb-4 flex flex-wrap items-center gap-2">
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          {subsection.title}
                          {subsection.tierLabel && (
                            <Badge variant="outline" className="text-[10px] font-normal">
                              {subsection.tierLabel.replace('_', '-')}
                            </Badge>
                          )}
                          {subsection.requiredRoles && subsection.requiredRoles.length > 0 && (
                            <Badge variant="secondary" className="text-[10px] font-normal">
                              {subsection.requiredRoles.map((r) => r.replace('_', ' ')).join(' · ')}
                            </Badge>
                          )}
                        </h3>
                        <div className="prose prose-sm max-w-none dark:prose-invert">
                          {subsection.content}
                        </div>
                      </div>
                    ))}
                  </div>

                  {section.id !== filteredSections[filteredSections.length - 1].id && (
                    <Separator className="mt-12" />
                  )}
                </section>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-12 p-6 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">Need More Help?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Can't find what you're looking for? Contact our support team for assistance.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" size="sm">
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-2" />
                Submit Feedback
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          size="icon"
          className="fixed bottom-6 right-6 rounded-full shadow-lg z-50"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default UserGuide;
